// =============================================================================
// SIREPPAR - Wizard público de constitución de alianza (arts. 26-27 Ley XII-23)
// =============================================================================
// Seis pasos: 1) convocatoria e integrantes, 2) identificación y atributos,
// 3) documentación del Art. 27, 4) apoderados y acceso, 5) órganos, 6) revisión.
//
// DIFERENCIA CON LOS OTROS WIZARDS EXTERNOS
// -----------------------------------------
// Los wizards de constitución de partido, reempadronamiento y reconocimiento
// arman TODO en `useState` y recién al final hacen un POST grande, con el cartel
// de "no cierre la página". Acá no: el borrador vive en el servidor desde el
// primer paso, cada cambio se guarda con un PATCH con debounce y el estado de
// guardado se muestra en el encabezado. Salir y volver por el link del borrador
// recupera exactamente lo cargado.
//
// COLOR DE ESTA PANTALLA
// ----------------------
// Fondo en degradado `from-purple-50 to-purple-100` y todo el acento en la rampa
// purple, con los mismos valores que la tarjeta "Constituir nueva alianza" del
// landing (purple-700 de base, purple-800 en hover). Es el mismo criterio que sigue
// el wizard de constitución de partido, que va en ámbar porque su tarjeta del
// landing es ámbar: cada trámite externo hereda el color de su tarjeta, así se lee
// como continuación de donde se hizo clic. Por eso los botones acá NO son azules
// como pide la regla general de UI del proyecto.
//
// Los verdes, rojos y ámbar del checklist y las validaciones se quedan: son
// semánticos (cargado / falta / aviso), no color de marca.
//
// La fuente de verdad es el servidor: cada mutación devuelve la alianza completa
// y con eso se re-renderiza. El `useState` local solo sostiene los campos de texto
// del paso 2 mientras se tipean (para no perder el foco en cada respuesta) y la
// contraseña, que a propósito NUNCA se persiste en el borrador: se pide en el paso
// 4, vive en memoria y viaja recién en el envío. Si se recarga la página, el paso
// 6 la vuelve a pedir.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { validarArchivo, ACCEPT_DOCUMENTOS, mimeDesdeExtension } from '../../utils/archivos';
import { alianzaService, AlianzaArchivoSubido, api, padronService } from '../../services/api';
import { useLocalidades } from '../../hooks/useLocalidades';
import AgrupacionSelect from '../../components/common/AgrupacionSelect';
import { formatFechaCalendario, formatMarcaTiempo } from '../../utils/fechas';

// -----------------------------------------------------------------------------
// Tipos del agregado que devuelve el backend
// -----------------------------------------------------------------------------

interface ConvocatoriaAbierta {
  id: string;
  denominacion: string;
  codigoCorto: string;
  fechaActo: string;
  ambitoTipo: string;
  cargo: string;
  localidad: string | null;
  cierreAlianzas: string | null;
}

interface PartidoElegible {
  id: string;
  denominacion: string;
  sigla: string | null;
  tipo: string;
  localidad: string | null;
  estado: string;
}

interface Integrante {
  id: string;
  miembroId: string | null;
  miembroNoMigradoDenominacion: string | null;
  miembroNoMigradoTipo: string | null;
  actaVoluntadDocumentoId: string | null;
  miembro: { id: string; denominacion: string; sigla: string | null; tipo: string; localidad: string | null; estado: string } | null;
  miembroNoMigradoLocalidad: { id: number; nombre: string } | null;
  actaVoluntadDocumento: { id: string; nombreOriginal: string; creadoEn: string; version: number } | null;
}

interface AutoridadOrgano {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  genero: string;
  /** ISO del backend. `Autoridad.fechaNacimiento` tiene columna propia. */
  fechaNacimiento: string | null;
  mandatos: Array<{ id: string; cargo: string | null; esTitular: boolean; ordenSuplencia: number | null }>;
}

interface Organo {
  id: string;
  tipoOrgano: string;
  denominacion: string;
  denominacionPersonalizada: string | null;
  esObligatorio: boolean;
  autoridades: AutoridadOrgano[];
}

interface ApoderadoAlianza {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  email: string | null;
  telefono: string | null;
  genero: string | null;
}

interface DocumentoAlianza {
  id: string;
  tipo: string;
  titulo: string;
  nombreOriginal: string;
  version: number;
  creadoEn: string;
  dniPersona: string | null;
  tamanioBytes: number;
}

interface AlianzaCompleta {
  alianza: {
    id: string;
    numero: number;
    denominacion: string;
    sigla: string | null;
    tipo: string;
    estado: string;
    domicilioLegal: string | null;
    localidad: string | null;
    codigoPostal: string | null;
    emailOficial: string | null;
    telefonoOficial: string | null;
    convocatoria: {
      id: string;
      denominacion: string;
      codigoCorto: string;
      fechaActo: string;
      ambitoTipo: string;
      localidadId: number | null;
      cierreAlianzas: string | null;
    } | null;
  };
  expediente: {
    id: string;
    numero: string;
    estado: string;
    fechaPresentacion: string | null;
    editable: boolean;
  };
  integrantes: Integrante[];
  organos: Organo[];
  apoderados: ApoderadoAlianza[];
  documentos: DocumentoAlianza[];
  borrador: {
    pasoMaximo: number;
    atributos: { colores: string[]; descripcionSimbolo?: string };
    designantes: { apoderados: Record<string, string>; autoridadesOrgano: Record<string, string> };
    /** Datos del apoderado sin columna propia. Hoy solo la fecha de nacimiento. */
    personasApoderado: Record<string, { fechaNacimiento?: string }>;
    apoderadoAccesoId?: string;
  };
}

interface Validacion {
  clave: string;
  etiqueta: string;
  estado: 'ok' | 'falta' | 'aviso';
  detalle?: string;
  paso: number;
}

// -----------------------------------------------------------------------------
// Constantes
// -----------------------------------------------------------------------------

const PASOS = [
  'Convocatoria e integrantes',
  'Identificación de la alianza',
  'Documentación Art. 27',
  'Apoderados y acceso',
  'Órganos de la alianza',
  'Revisión y envío',
];

/** Mismos cargos que ofrecen los wizards de reempadronamiento y reconocimiento. */
const CARGOS = ['PRESIDENTE', 'VICEPRESIDENTE', 'SECRETARIO', 'PROSECRETARIO', 'TESORERO', 'PROTESORERO', 'VOCAL', 'OTRO'];
const cargoLabel = (c: string) => c.charAt(0) + c.slice(1).toLowerCase();

const TIPOS_INTEGRANTE = [
  { value: 'MUNICIPAL', label: 'Municipal' },
  { value: 'DISTRITO', label: 'Distrital' },
  { value: 'PROVINCIAL', label: 'Provincial' },
];
const tipoLabel = (t?: string | null) => TIPOS_INTEGRANTE.find((x) => x.value === t)?.label || t || '—';

/** Orden en que se listan los partidos elegibles del registro. */
const ORDEN_TIPO = ['MUNICIPAL', 'DISTRITO', 'PROVINCIAL'];

const MAX_COLORES = 6;
const MAX_SIMBOLOS = 1;

/** Los cinco documentos del Art. 27 que no son actas de voluntad. */
const DOCUMENTOS_ART_27 = [
  {
    tipo: 'PLATAFORMA_ELECTORAL',
    titulo: 'Plataforma electoral común',
    articulo: 'Art. 27 inc. c) · documento único aprobado por todos los partidos integrantes',
    explicacion: 'Programa político común que la alianza va a presentar al electorado, con las propuestas y lineamientos compartidos por los partidos aliados. Es distinta de la plataforma individual de cada partido, aunque puede coincidir con la de alguno.',
  },
  {
    tipo: 'ACUERDO_CONSTITUTIVO',
    titulo: 'Acuerdo constitutivo',
    articulo: 'Art. 27 inc. d)',
    explicacion: 'Documento donde los partidos declaran formalmente la constitución de la alianza: partidos integrantes, denominación, sigla, símbolos y colores, domicilio legal, y firma de las autoridades partidarias competentes de cada integrante.',
  },
  {
    tipo: 'ACUERDO_FUNCIONAMIENTO',
    titulo: 'Acuerdo de funcionamiento',
    articulo: 'Art. 27 inc. d)',
    explicacion: 'Reglas internas de la alianza durante el proceso electoral: cómo se toman las decisiones, quién la representa en cada instancia, cómo se resuelven los desacuerdos y cuáles son las obligaciones de cada partido.',
  },
  {
    tipo: 'ACUERDO_FINANCIERO',
    titulo: 'Acuerdo financiero',
    articulo: 'Art. 27 inc. d)',
    explicacion: 'Cómo se reparten entre los integrantes los aportes públicos (Art. 46), los aportes privados, los gastos de campaña y las eventuales sanciones económicas. Debe especificar porcentajes o criterios de reparto claros.',
  },
  {
    tipo: 'REGLAMENTO_ELECTORAL_ALIANZA',
    titulo: 'Constancia de la forma acordada para integrar las listas y reglamento electoral',
    articulo: 'Art. 27 inc. e) · sujeto a las pautas del Código Electoral',
    explicacion: 'Cómo va a seleccionar la alianza sus candidatos: elección interna, designación por consenso, cupos por partido, y cómo se ordenan las listas. Debe cumplir las reglas del Código Electoral (paridad, requisitos de candidatos).',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CLAVE_BORRADOR = 'sireppar.alianza.borrador';

// -----------------------------------------------------------------------------
// Piezas de UI compartidas
// -----------------------------------------------------------------------------

const FieldError = ({ error }: { error?: string }) =>
  error ? <p className="text-xs text-red-600 mt-1">⚠️ {error}</p> : null;

const Marca = ({ ok }: { ok: boolean }) => (
  <span
    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${
      ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}
    aria-label={ok ? 'Cargado' : 'Pendiente'}
  >
    {ok ? '✓' : '!'}
  </span>
);

/** Spinner inline. La regla de UI del proyecto pide indicador visible en cada request. */
const Spinner = ({ claro = false }: { claro?: boolean }) => (
  <svg className={`animate-spin w-4 h-4 mr-2 ${claro ? 'text-white' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const Etiqueta = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1">{children}</label>
);

const inputBase = 'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500';
const inputClass = (error?: string) => `${inputBase} ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;
/**
 * Campo ya guardado: gris y deshabilitado. Una persona cargada no se edita en su
 * lugar — se Quita y se vuelve a cargar. Ver la nota de TarjetaApoderado.
 */
const inputBloqueado = `${inputBase} border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed`;
/** Variante compacta, para las grillas de autoridades de órgano. */
const inputChicoBloqueado = 'w-full px-2 py-1.5 border border-gray-200 bg-gray-100 text-gray-500 rounded text-sm cursor-not-allowed';

const botonPrimario = 'inline-flex items-center px-5 py-2.5 bg-purple-700 text-white rounded-lg font-medium hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition';
const botonSecundario = 'inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition';

/** Sube un archivo a S3 por URL firmada y devuelve el descriptor del backend. */
async function subirArchivo(file: File): Promise<AlianzaArchivoSubido> {
  // Tope de peso y formato, con el mismo criterio y el mismo mensaje que el uploader
  // genérico del sistema (ver utils/archivos). Va acá y no en cada botón porque por
  // esta función pasan TODAS las subidas del wizard: los botones de documentación, el
  // acta y los DNI de cada apoderado y los DNI de cada autoridad de órgano. Validar
  // en los botones dejaba afuera la mitad.
  const problema = validarArchivo(file);
  if (problema) throw new Error(problema);

  const resp = await api.post('/storage/upload-urls', {
    files: [{ fileName: file.name, contentType: file.type || mimeDesdeExtension(file.name) }],
  });
  const firmadas: Array<{ url: string; path: string }> = resp.data.data;
  if (!firmadas?.length) throw new Error('No se recibió la URL firmada del almacenamiento');

  // fetch nativo, NO axios: la URL firmada no debe llevar el header Authorization
  // que el interceptor de axios agrega (invalidaría la firma).
  const puesta = await fetch(firmadas[0].url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || mimeDesdeExtension(file.name) },
  });
  if (!puesta.ok) throw new Error(`El almacenamiento rechazó el archivo (HTTP ${puesta.status})`);

  return { path: firmadas[0].path, nombreOriginal: file.name, mimeType: file.type || undefined };
}

/** Botón de carga de archivo con estado de subida propio. */
function BotonArchivo({
  onArchivo,
  etiqueta,
  accept = ACCEPT_DOCUMENTOS,
  disabled,
}: {
  onArchivo: (archivo: AlianzaArchivoSubido) => Promise<void> | void;
  etiqueta: string;
  accept?: string;
  disabled?: boolean;
}) {
  const [subiendo, setSubiendo] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const manejar = async (file?: File) => {
    if (!file) return;
    // Mismo tope y mismo mensaje que el uploader genérico del sistema. Se valida ACÁ,
    // antes de pedir la URL firmada: si no, un archivo enorme empieza a subir y la
    // persona se queda esperando sin saber por qué falla.
    const problema = validarArchivo(file, { accept });
    if (problema) {
      toast.error(problema);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setSubiendo(true);

    try {
      const archivo = await subirArchivo(file);
      await onArchivo(archivo);
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'No se pudo subir el archivo');
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled || subiendo}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center px-3 py-1.5 text-sm bg-purple-700 text-white rounded-lg hover:bg-purple-800 disabled:opacity-50 transition"
      >
        {subiendo && <Spinner claro />}
        {subiendo ? 'Subiendo…' : etiqueta}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => manejar(e.target.files?.[0])}
      />
    </>
  );
}

// =============================================================================
// Página
// =============================================================================

export default function ConstitucionAlianzaPage() {
  const { alianzaId } = useParams<{ alianzaId: string }>();
  const navigate = useNavigate();

  const [datos, setDatos] = useState<AlianzaCompleta | null>(null);
  const [cargando, setCargando] = useState(true);
  const [pasoActual, setPasoActual] = useState(0);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [estadoGuardado, setEstadoGuardado] = useState<'idle' | 'guardando' | 'guardado' | 'error'>('idle');
  const [envio, setEnvio] = useState<{
    expedienteNumero: string;
    accesoPendienteVinculacion: boolean;
    constanciaPdfBase64?: string;
  } | null>(null);

  // La contraseña vive solo en memoria (ver nota del encabezado del archivo).
  const [credenciales, setCredenciales] = useState({ email: '', password: '', passwordConfirmacion: '' });

  // DNI del apoderado que recibe el acceso: define si hay que pedir credenciales
  // nuevas o si la persona entra con el usuario que ya tiene.
  const dniTitularAcceso = datos
    ? datos.apoderados.find((a) => a.id === (datos.borrador.apoderadoAccesoId || datos.apoderados[0]?.id))?.dni
    : undefined;
  const accesoTitular = useAccesoExistente(dniTitularAcceso);
  /** True cuando la persona ya tiene usuario: no se le piden correo ni contraseña. */
  const reutilizaCuenta = accesoTitular.tieneUsuario === true;

  // ---- Carga inicial ----
  const recargar = useCallback(async () => {
    if (!alianzaId) return;
    const r = await alianzaService.obtener(alianzaId);
    const completo: AlianzaCompleta = r.data.data;
    setDatos(completo);
    return completo;
  }, [alianzaId]);

  useEffect(() => {
    if (!alianzaId) return;
    let vivo = true;
    setCargando(true);
    alianzaService
      .obtener(alianzaId)
      .then((r) => {
        if (!vivo) return;
        const completo: AlianzaCompleta = r.data.data;
        setDatos(completo);
        // Se reabre donde quedó, no en el paso 1.
        setPasoActual(Math.min(completo.borrador.pasoMaximo, PASOS.length - 1));
        setCredenciales((c) => ({ ...c, email: c.email || completo.apoderados[0]?.email || '' }));
        localStorage.setItem(CLAVE_BORRADOR, alianzaId);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error?.message || 'No se pudo abrir el borrador de la alianza');
        navigate('/alianzas/nueva', { replace: true });
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [alianzaId, navigate]);

  // ---- Autoguardado con debounce ----
  const pendiente = useRef<Record<string, unknown>>({});
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  const guardar = useCallback(
    (cambios: Record<string, unknown>) => {
      if (!alianzaId) return;
      pendiente.current = { ...pendiente.current, ...cambios };
      setEstadoGuardado('guardando');
      if (temporizador.current) clearTimeout(temporizador.current);
      temporizador.current = setTimeout(async () => {
        const aEnviar = pendiente.current;
        pendiente.current = {};
        try {
          const r = await alianzaService.actualizar(alianzaId, aEnviar);
          setDatos(r.data.data);
          setEstadoGuardado('guardado');
        } catch (error: any) {
          setEstadoGuardado('error');
          toast.error(error?.response?.data?.error?.message || 'No se pudo guardar el borrador');
        }
      }, 800);
    },
    [alianzaId],
  );

  // Descarga el debounce pendiente al desmontar para no perder el último cambio.
  useEffect(() => () => {
    if (temporizador.current) clearTimeout(temporizador.current);
  }, []);

  /** Fuerza el guardado inmediato de lo pendiente (al cambiar de paso o enviar). */
  const guardarYa = useCallback(async () => {
    if (!alianzaId) return;
    if (temporizador.current) clearTimeout(temporizador.current);
    const aEnviar = pendiente.current;
    pendiente.current = {};
    if (Object.keys(aEnviar).length === 0) return;
    try {
      const r = await alianzaService.actualizar(alianzaId, aEnviar);
      setDatos(r.data.data);
      setEstadoGuardado('guardado');
    } catch (error: any) {
      setEstadoGuardado('error');
      toast.error(error?.response?.data?.error?.message || 'No se pudo guardar el borrador');
    }
  }, [alianzaId]);

  /** Ejecuta una mutación que devuelve la alianza completa. */
  const mutar = useCallback(async (accion: () => Promise<any>, mensajeOk?: string) => {
    try {
      const r = await accion();
      if (r?.data?.data?.alianza) setDatos(r.data.data);
      else await recargar();
      if (mensajeOk) toast.success(mensajeOk);
      return true;
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'No se pudo completar la operación');
      return false;
    }
  }, [recargar]);

  // ---- Validación por paso (bloquea el "Continuar") ----
  const validarPaso = (paso: number): boolean => {
    if (!datos) return false;
    const err: Record<string, string> = {};

    if (paso === 0 && datos.integrantes.length < 2) {
      err['integrantes'] = 'Una alianza requiere al menos dos partidos integrantes';
    }
    if (paso === 1) {
      const a = datos.alianza;
      if (!a.denominacion || a.denominacion.startsWith('Alianza en formación')) err['denominacion'] = 'La denominación es obligatoria';
      if (!a.sigla) err['sigla'] = 'La sigla es obligatoria';
      if (!a.domicilioLegal) err['domicilioLegal'] = 'El domicilio legal es obligatorio';
      if (!a.localidad) err['localidad'] = 'La localidad es obligatoria';
      if (!a.codigoPostal) err['codigoPostal'] = 'El código postal es obligatorio';
      if (!a.emailOficial || !EMAIL_RE.test(a.emailOficial)) err['emailOficial'] = 'El correo institucional no es válido';
    }
    if (paso === 2) {
      const sinActa = datos.integrantes.filter((i) => !i.actaVoluntadDocumentoId);
      if (sinActa.length > 0) err['actas'] = `Faltan ${sinActa.length} constancia(s) de la voluntad de aliarse`;
      const cargados = new Set(datos.documentos.map((d) => d.tipo));
      const faltantes = DOCUMENTOS_ART_27.filter((d) => !cargados.has(d.tipo));
      if (faltantes.length > 0) err['documentos'] = `Faltan ${faltantes.length} documento(s) del Art. 27`;
    }
    if (paso === 3) {
      if (datos.apoderados.length === 0) err['apoderados'] = 'Debe cargar el apoderado iniciador';
      else {
        // Las mismas exigencias que aplica el gate del envío, para que "Continuar"
        // y el checklist del paso 6 no digan cosas distintas.
        const titular = datos.apoderados.find((a) => a.id === (datos.borrador.apoderadoAccesoId || datos.apoderados[0].id));
        if (titular) {
          if (!titular.genero || titular.genero === 'OTRO') err['apoderados'] = 'Falta el género del apoderado iniciador';
          if (!datos.borrador.personasApoderado?.[titular.id]?.fechaNacimiento) {
            err['apoderados'] = 'Falta la fecha de nacimiento del apoderado iniciador';
          }
          const tiene = (tipo: string) => datos.documentos.some((d) => d.tipo === tipo && d.dniPersona === titular.dni);
          if (!tiene('ACTA_DESIGNACION_APODERADOS')) err['apoderados'] = 'Falta el acta de designación del apoderado iniciador';
          if (!tiene('DNI_FRENTE_APODERADO') || !tiene('DNI_DORSO_APODERADO')) {
            err['apoderados'] = 'Falta el frente o el dorso del DNI del apoderado iniciador';
          }
        }
      }
      // Si la persona ya tiene usuario, no se le pidieron correo ni contraseña: entra
      // con su cuenta de siempre y no hay nada que validar acá.
      if (!reutilizaCuenta) {
        if (!credenciales.email || !EMAIL_RE.test(credenciales.email)) err['acceso.email'] = 'El correo de acceso no es válido';
        if (!credenciales.password || credenciales.password.length < 8 || !/[a-zA-Z]/.test(credenciales.password) || !/\d/.test(credenciales.password)) {
          err['acceso.password'] = 'Mínimo 8 caracteres, con letras y números';
        }
        if (credenciales.password !== credenciales.passwordConfirmacion) err['acceso.passwordConfirmacion'] = 'Las contraseñas no coinciden';
      }
    }
    if (paso === 4) {
      const obligatorio = datos.organos.find((o) => o.esObligatorio);
      if (!obligatorio || obligatorio.autoridades.length === 0) err['organo'] = 'El órgano obligatorio necesita al menos una autoridad';
      // Ningún órgano declarado puede quedar vacío, ni el obligatorio ni los
      // adicionales: mismas reglas que aplica el gate del envío.
      const actas = new Set(
        datos.documentos
          .filter((d) => d.tipo === 'ACTA_DESIGNACION_ORGANO_ALIANZA' && d.dniPersona?.startsWith('ORGANO:'))
          .map((d) => (d.dniPersona as string).slice('ORGANO:'.length)),
      );
      const incompletos = datos.organos.filter((o) => o.autoridades.length === 0 || !actas.has(o.id));
      if (incompletos.length > 0) {
        const nombres = incompletos.map((o) => o.denominacionPersonalizada || o.denominacion).join(', ');
        err['organo'] = `Falta completar (autoridades y acta de designación): ${nombres}`;
      }
    }

    setErrores(err);
    return Object.keys(err).length === 0;
  };

  const irAPaso = async (paso: number) => {
    await guardarYa();
    setErrores({});
    setPasoActual(paso);
    window.scrollTo(0, 0);
  };

  const siguiente = async () => {
    if (!validarPaso(pasoActual)) {
      toast.error('Revise los datos marcados en rojo');
      return;
    }
    const proximo = Math.min(pasoActual + 1, PASOS.length - 1);
    guardar({ pasoMaximo: proximo });
    await irAPaso(proximo);
  };

  // ---- Envío ----
  const [enviando, setEnviando] = useState(false);
  const [aceptaDeclaracion, setAceptaDeclaracion] = useState(false);

  const enviar = async () => {
    if (!alianzaId || !datos) return;
    if (!aceptaDeclaracion) {
      toast.error('Debe aceptar la declaración jurada para enviar');
      return;
    }
    if (!validarPaso(3)) {
      toast.error('Faltan datos del apoderado iniciador o de su cuenta de acceso');
      setPasoActual(3);
      return;
    }
    setEnviando(true);
    try {
      await guardarYa();
      const r = await alianzaService.enviar(alianzaId, reutilizaCuenta ? undefined : credenciales);
      setEnvio(r.data.data);
      localStorage.removeItem(CLAVE_BORRADOR);
      window.scrollTo(0, 0);
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'No se pudo enviar la solicitud');
    } finally {
      setEnviando(false);
    }
  };

  // ---- Render ----
  if (!alianzaId) return <SeleccionConvocatoria />;

  if (cargando || !datos) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center">
        <p className="text-gray-500 flex items-center"><Spinner />Abriendo el borrador…</p>
      </div>
    );
  }

  if (envio) {
    return <PantallaExito envio={envio} alianzaId={alianzaId} denominacion={datos.alianza.denominacion} />;
  }

  if (!datos.expediente.editable) {
    return (
      <ExpedientePresentado
        expediente={datos.expediente}
        denominacion={datos.alianza.denominacion}
        alianzaId={alianzaId}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100">
      {/* Encabezado */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Constitución de alianza electoral</h1>
            <p className="text-sm text-gray-500">
              Paso {pasoActual + 1} de {PASOS.length} · {PASOS[pasoActual]}
              {datos.alianza.convocatoria ? ` · ${datos.alianza.convocatoria.denominacion}` : ''}
            </p>
          </div>
          <EstadoBorrador estado={estadoGuardado} />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6">
        {/* Barra lateral: los seis pasos del trámite externo. Queda fija al
            scrollear y TODOS los pasos son navegables: se puede mirar lo que
            viene sin haberlo completado. La validación no desaparece, vive en
            "Continuar" y en el gate del envío. */}
        <nav className="lg:w-64 flex-shrink-0">
          <div className="lg:sticky lg:top-6 space-y-3">
            <ol className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
              {PASOS.map((titulo, i) => {
                const visitado = i <= Math.max(datos.borrador.pasoMaximo, pasoActual);
                const activo = i === pasoActual;
                return (
                  <li key={titulo}>
                    <button
                      type="button"
                      onClick={() => irAPaso(i)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition ${
                        activo ? 'bg-purple-50 text-purple-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
                          activo ? 'bg-purple-700 text-white' : visitado ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className={activo || visitado ? '' : 'text-gray-500'}>{titulo}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                <span>Paso {pasoActual + 1} de {PASOS.length}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-700 rounded-full transition-all"
                  style={{ width: `${((pasoActual + 1) / PASOS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </nav>

        {/* Contenido del paso */}
        <main className="flex-1 min-w-0">
          {pasoActual === 0 && (
            <Paso1Integrantes datos={datos} errores={errores} mutar={mutar} />
          )}
          {pasoActual === 1 && (
            <Paso2Identificacion datos={datos} errores={errores} guardar={guardar} mutar={mutar} />
          )}
          {pasoActual === 2 && (
            <Paso3Documentacion datos={datos} errores={errores} mutar={mutar} />
          )}
          {pasoActual === 3 && (
            <Paso4Apoderados
              datos={datos}
              errores={errores}
              mutar={mutar}
              credenciales={credenciales}
              setCredenciales={setCredenciales}
            />
          )}
          {pasoActual === 4 && (
            <Paso5Organos datos={datos} errores={errores} mutar={mutar} />
          )}
          {pasoActual === 5 && (
            <Paso6Revision
              datos={datos}
              alianzaId={alianzaId}
              credencialesDefinidas={reutilizaCuenta || !!credenciales.password}
              reutilizaCuenta={reutilizaCuenta}
              credenciales={credenciales}
              setCredenciales={setCredenciales}
              errores={errores}
              aceptaDeclaracion={aceptaDeclaracion}
              setAceptaDeclaracion={setAceptaDeclaracion}
              enviando={enviando}
              onEnviar={enviar}
              onEditar={irAPaso}
            />
          )}

          {/* Botonera */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => irAPaso(Math.max(pasoActual - 1, 0))}
              disabled={pasoActual === 0}
              className={`${botonSecundario} disabled:opacity-40`}
            >
              Volver
            </button>
            <div className="flex items-center gap-3">
              <button type="button" onClick={guardarYa} className={botonSecundario}>
                Guardar borrador
              </button>
              {pasoActual < PASOS.length - 1 && (
                <button type="button" onClick={siguiente} className={botonPrimario}>
                  Continuar
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Indicador de guardado
// -----------------------------------------------------------------------------

function EstadoBorrador({ estado }: { estado: 'idle' | 'guardando' | 'guardado' | 'error' }) {
  const texto =
    estado === 'guardando' ? 'Guardando…'
    : estado === 'guardado' ? 'Borrador guardado'
    : estado === 'error' ? 'No se pudo guardar'
    : 'Borrador — se guarda automáticamente';
  const clase =
    estado === 'error' ? 'bg-red-50 text-red-700 border-red-200'
    : estado === 'guardado' ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return <span className={`text-xs px-3 py-1.5 rounded-full border ${clase}`}>{texto}</span>;
}

// -----------------------------------------------------------------------------
// Pantalla previa: elegir convocatoria y crear el borrador
// -----------------------------------------------------------------------------

function SeleccionConvocatoria() {
  const navigate = useNavigate();
  const [convocatorias, setConvocatorias] = useState<ConvocatoriaAbierta[]>([]);
  const [convocatoriaId, setConvocatoriaId] = useState('');
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  const borradorGuardado = localStorage.getItem(CLAVE_BORRADOR);

  useEffect(() => {
    alianzaService
      .convocatoriasAbiertas()
      .then((r) => {
        const lista: ConvocatoriaAbierta[] = r.data.data;
        setConvocatorias(lista);
        if (lista.length === 1) setConvocatoriaId(lista[0].id);
      })
      .catch(() => toast.error('No se pudieron cargar las convocatorias'))
      .finally(() => setCargando(false));
  }, []);

  const comenzar = async () => {
    if (!convocatoriaId) {
      toast.error('Seleccione la convocatoria para la que se constituye la alianza');
      return;
    }
    setCreando(true);
    try {
      const r = await alianzaService.crearBorrador(convocatoriaId);
      const { alianzaId } = r.data.data;
      localStorage.setItem(CLAVE_BORRADOR, alianzaId);
      navigate(`/alianzas/${alianzaId}`, { replace: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'No se pudo iniciar el trámite');
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <button type="button" onClick={() => navigate('/login')} className="text-sm text-gray-500 hover:text-gray-700 mb-4">
          ← Volver al inicio
        </button>

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-purple-700 px-6 py-5">
            <h1 className="text-xl font-bold text-white">Constituir una nueva alianza electoral</h1>
            <p className="text-purple-100 text-sm mt-1">Arts. 26 y 27 — Ley XII N° 23</p>
          </div>

          <div className="p-6 space-y-5">
            <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded-r-lg text-sm text-purple-900">
              <p className="font-semibold mb-1">Antes de comenzar</p>
              <p className="text-purple-800 mb-2">
                Tenga a mano la documentación. El trámite se guarda como borrador y puede completarlo
                en varias sesiones, pero no se puede presentar incompleto.
              </p>

              <p className="font-semibold mt-3">Documentación exigida por el Art. 27</p>
              <ul className="list-disc pl-5 space-y-1 text-purple-800">
                <li>
                  <strong>Constancia de la voluntad de aliarse / Autorizacion partidaria 
                    para constituir alianza</strong> — una por cada partido integrante,
                   <em>(inc. a)</em>.
                </li>
                <li><strong>Plataforma electoral común</strong> <em>(inc. c)</em>.</li>
                <li><strong>Acuerdo constitutivo</strong> <em>(inc. d)</em>.</li>
                <li><strong>Acuerdo de funcionamiento</strong> <em>(inc. d)</em>.</li>
                <li><strong>Acuerdo financiero</strong> <em>(inc. d)</em>.</li>
                <li>
                  <strong>Forma acordada de integración de las listas y reglamento electoral</strong> <em>(inc. e)</em>.
                </li>
              </ul>

              <p className="font-semibold mt-3">Datos y documentación complementaria</p>
              <ul className="list-disc pl-5 space-y-1 text-purple-800">
                <li>
                  Por cada <strong>apoderado</strong>: datos personales, acta de designación y frente y dorso
                  del DNI.
                </li>
                <li>
                  Por cada <strong>órgano</strong> (la Junta Electoral es obligatoria): su acta de designación
                  y, por cada autoridad, frente y dorso del DNI.
                </li>
                <li>Opcional en esta etapa: emblema, símbolos (hasta 3) y colores.</li>
              </ul>

              <p className="text-purple-800 mt-3 border-t border-purple-200 pt-2">
                Las <strong>Cartas Orgánicas no se adjuntan</strong>: ya obran en poder de la Secretaría.
                Pero tienen que autorizar la formación de alianzas <em>(Art. 26)</em>, y eso se declara
                bajo juramento al enviar.
              </p>
            </div>

            {borradorGuardado && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-amber-800">Tiene un borrador de alianza empezado en este navegador.</p>
                <button type="button" onClick={() => navigate(`/alianzas/${borradorGuardado}`)} className={botonSecundario}>
                  Continuar ese borrador
                </button>
              </div>
            )}

            <div>
              <Etiqueta>Convocatoria electoral *</Etiqueta>
              {cargando ? (
                <p className="text-sm text-gray-500">Cargando convocatorias…</p>
              ) : convocatorias.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                  No hay convocatorias con el plazo de constitución de alianzas vigente. El plazo cierra 90 días antes
                  del acto electoral (Art. 26).
                </div>
              ) : (
                <>
                  <select
                    value={convocatoriaId}
                    onChange={(e) => setConvocatoriaId(e.target.value)}
                    className={inputClass()}
                  >
                    <option value="">Seleccione la convocatoria…</option>
                    {convocatorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.denominacion} ({c.codigoCorto})
                      </option>
                    ))}
                  </select>
                  {convocatoriaId && (
                    <DetalleConvocatoria convocatoria={convocatorias.find((c) => c.id === convocatoriaId)!} />
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              onClick={comenzar}
              disabled={creando || !convocatoriaId}
              className={botonPrimario}
            >
              {creando && <Spinner claro />}
              {creando ? 'Creando borrador…' : 'Comenzar el trámite'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Las dos fechas de acá NO son la misma clase de dato y por eso se formatean
// distinto: `fechaActo` es una fecha de calendario (el día del acto, sin hora), y
// `cierreAlianzas` es un instante real —el backend lo devuelve como fin del día en
// hora argentina (ver cierreAlianzas() en alianzaService.ts)—, así que ese sí se
// convierte a la zona de Chubut.
function DetalleConvocatoria({ convocatoria }: { convocatoria: ConvocatoriaAbierta }) {
  return (
    <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
      <p><span className="font-medium">Cargo:</span> {convocatoria.cargo}</p>
      <p><span className="font-medium">Ámbito:</span> {convocatoria.ambitoTipo}{convocatoria.localidad ? ` — ${convocatoria.localidad}` : ''}</p>
      <p><span className="font-medium">Acto electoral:</span> {formatFechaCalendario(convocatoria.fechaActo)}</p>
      <p><span className="font-medium">Cierre para constituir alianzas:</span> {formatMarcaTiempo(convocatoria.cierreAlianzas)}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tipos comunes de los pasos
// -----------------------------------------------------------------------------

type Mutar = (accion: () => Promise<any>, mensajeOk?: string) => Promise<boolean>;

interface PropsPaso {
  datos: AlianzaCompleta;
  errores: Record<string, string>;
  mutar: Mutar;
}

// Todas las secciones del trámite llevan el mismo filo violeta a la izquierda: es
// la marca de la pantalla, no un estado. Lo que distingue a una sección especial
// (el órgano obligatorio, el apoderado titular del acceso) es su insignia, no el
// borde.
const Tarjeta = ({ titulo, subtitulo, children, accion }: {
  titulo: string;
  subtitulo?: string;
  children: React.ReactNode;
  accion?: React.ReactNode;
}) => (
  <section className="bg-white rounded-xl border border-gray-200 border-l-4 border-l-purple-700 mb-5">
    <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="font-semibold text-gray-900">{titulo}</h2>
        {subtitulo && <p className="text-sm text-gray-500 mt-0.5">{subtitulo}</p>}
      </div>
      {accion}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

// =============================================================================
// PASO 1 — Convocatoria e integrantes
// =============================================================================

function Paso1Integrantes({ datos, errores, mutar }: PropsPaso) {
  const { localidades } = useLocalidades();
  const [elegibles, setElegibles] = useState<PartidoElegible[]>([]);
  const [noResueltas, setNoResueltas] = useState<Array<{ id: string; denominacion: string; localidad: string | null }>>([]);
  const [seleccionado, setSeleccionado] = useState('');
  const [otros, setOtros] = useState({ denominacion: '', tipo: '', localidadId: '' });
  const [ocupado, setOcupado] = useState(false);

  const convocatoriaId = datos.alianza.convocatoria?.id;

  useEffect(() => {
    if (!convocatoriaId) return;
    alianzaService
      .partidosElegibles(convocatoriaId)
      .then((r) => {
        setElegibles(r.data.data.elegibles || []);
        setNoResueltas(r.data.data.localidadesNoResueltas || []);
      })
      .catch(() => toast.error('No se pudo cargar el listado de partidos elegibles'));
  }, [convocatoriaId]);

  // Convocatoria municipal: la localidad de un integrante municipal no se elige,
  // es la de la convocatoria (única que puede presentar candidatos en esa elección,
  // misma regla que filtra el desplegable de arriba). En una convocatoria provincial
  // sí entran municipales de cualquier localidad, así que ahí se elige.
  const convocatoriaEsMunicipal = datos.alianza.convocatoria?.ambitoTipo === 'MUNICIPAL';
  const localidadFijaId = convocatoriaEsMunicipal ? datos.alianza.convocatoria?.localidadId ?? null : null;
  const localidadFijaNombre = localidadFijaId != null
    ? localidades.find((l) => l.id === localidadFijaId)?.nombre ?? ''
    : '';

  const yaIntegrantes = useMemo(
    () => new Set(datos.integrantes.map((i) => i.miembroId).filter(Boolean) as string[]),
    [datos.integrantes],
  );

  const disponibles = elegibles.filter((p) => !yaIntegrantes.has(p.id));
  // Ordenadas como las mostraban los optgroup (municipales, de distrito,
  // provinciales) y alfabéticamente dentro de cada grupo.
  const opcionesPartidos = useMemo(
    () => [...disponibles]
      .sort((a, b) => {
        const t = ORDEN_TIPO.indexOf(a.tipo) - ORDEN_TIPO.indexOf(b.tipo);
        return t !== 0 ? t : a.denominacion.localeCompare(b.denominacion, 'es');
      })
      .map((p) => ({
        id: p.id,
        denominacion: p.denominacion,
        sigla: p.sigla,
        detalle: [tipoLabel(p.tipo), p.localidad].filter(Boolean).join(' · '),
      })),
    [disponibles],
  );

  const agregarPartido = async () => {
    if (!seleccionado) return;
    setOcupado(true);
    const ok = await mutar(() => alianzaService.agregarIntegrantePartido(datos.alianza.id, seleccionado), 'Integrante agregado');
    if (ok) setSeleccionado('');
    setOcupado(false);
  };

  const agregarOtros = async () => {
    if (!otros.denominacion.trim() || !otros.tipo) {
      toast.error('Complete la denominación y el ámbito del partido');
      return;
    }
    const localidadElegida = localidadFijaId ?? (otros.localidadId ? Number(otros.localidadId) : undefined);
    if (otros.tipo === 'MUNICIPAL' && !localidadElegida) {
      toast.error('Un partido municipal requiere indicar la localidad');
      return;
    }
    setOcupado(true);
    const ok = await mutar(
      () => alianzaService.agregarIntegranteOtros(datos.alianza.id, {
        miembroNoMigradoDenominacion: otros.denominacion.trim(),
        miembroNoMigradoTipo: otros.tipo,
        miembroNoMigradoLocalidadId: otros.tipo === 'MUNICIPAL' ? localidadElegida : undefined,
      }),
      'Integrante agregado',
    );
    if (ok) setOtros({ denominacion: '', tipo: '', localidadId: '' });
    setOcupado(false);
  };

  return (
    <>
      <div className="bg-white border border-gray-200 border-l-4 border-l-purple-700 p-4 rounded-r-lg mb-5 text-sm text-purple-900 shadow-sm">
        <p className="font-semibold mb-1">Composiciones válidas — Art. 26</p>
        <p className="text-purple-800">
          El ámbito territorial de la alianza es el del integrante de mayor alcance: si hay al menos un partido provincial la
          alianza es provincial; si hay uno distrital, es distrital; en los demás casos, municipal.
        </p>
      </div>

      <Tarjeta
        titulo="Convocatoria"
      >
        {datos.alianza.convocatoria ? (
          <div className="text-sm text-gray-700 space-y-1">
            <p className="font-medium text-gray-900">{datos.alianza.convocatoria.denominacion}</p>
            <p>Código: {datos.alianza.convocatoria.codigoCorto}</p>
            <p>
              Cierre para constituir alianzas:{' '}
              {formatMarcaTiempo(datos.alianza.convocatoria.cierreAlianzas)}
            </p>
            <p className="mt-2">
              <span className="font-medium">Ámbito derivado de la alianza:</span>{' '}
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-semibold">
                {tipoLabel(datos.alianza.tipo)}
              </span>
            </p>
          </div>
        ) : (
          <p className="text-sm text-red-600">La alianza no tiene convocatoria asociada.</p>
        )}
      </Tarjeta>

      <Tarjeta titulo={`Partidos integrantes (${datos.integrantes.length})`} subtitulo="Mínimo dos para poder continuar.">
        {datos.integrantes.length === 0 ? (
          <p className="text-sm text-gray-500">Todavía no agregó ningún partido integrante.</p>
        ) : (
          <ul className="space-y-2">
            {datos.integrantes.map((i) => {
              const nombre = i.miembro?.denominacion || i.miembroNoMigradoDenominacion || 'Sin denominación';
              const tipo = i.miembro?.tipo || i.miembroNoMigradoTipo;
              const esOtros = !i.miembroId;
              return (
                <li
                  key={i.id}
                  className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border ${
                    esOtros ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{nombre}</p>
                    <p className="text-xs text-gray-600">
                      {tipoLabel(tipo)}
                      {i.miembro?.localidad ? ` · ${i.miembro.localidad}` : ''}
                      {i.miembroNoMigradoLocalidad ? ` · ${i.miembroNoMigradoLocalidad.nombre}` : ''}
                      {esOtros ? ' · declarado como no registrado en SIREPPAR' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => mutar(() => alianzaService.quitarIntegrante(datos.alianza.id, i.id), 'Integrante quitado')}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Quitar
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <FieldError error={errores['integrantes']} />
      </Tarjeta>

      <Tarjeta titulo="Agregar un partido del registro">
        <div className="flex flex-wrap gap-3">
          {/*
            Selector con buscador en lugar del <select> nativo. El nativo abría una
            lista tan alta como partidos hubiera y se iba de la pantalla; este panel
            se recorta al espacio visible y scrollea adentro. El tipo y la localidad
            van en la segunda línea de cada opción, que es lo que antes daban los
            optgroup y el sufijo del label.
          */}
          <div className="flex-1 min-w-[240px]">
            <AgrupacionSelect
              value={seleccionado}
              onChange={setSeleccionado}
              opciones={opcionesPartidos}
              placeholder="Buscá por nombre, sigla o localidad…"
            />
          </div>
          <button type="button" onClick={agregarPartido} disabled={ocupado || !seleccionado} className={botonPrimario}>
            Agregar
          </button>
        </div>

      </Tarjeta>

      <Tarjeta
        titulo="¿El partido no aparece en el listado?"
        subtitulo="Solo para partidos que aun no estan reempadronados. La Secretaría lo coteja durante el análisis."
      >
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3">
            <Etiqueta>Denominación del partido *</Etiqueta>
            <input
              type="text"
              value={otros.denominacion}
              onChange={(e) => setOtros((o) => ({ ...o, denominacion: e.target.value }))}
              className={inputClass()}
              placeholder="Nombre completo del partido"
            />
          </div>
          <div>
            <Etiqueta>Ámbito *</Etiqueta>
            <select
              value={otros.tipo}
              onChange={(e) => setOtros((o) => ({ ...o, tipo: e.target.value, localidadId: e.target.value === 'MUNICIPAL' ? o.localidadId : '' }))}
              className={inputClass()}
            >
              <option value="">Seleccione…</option>
              {TIPOS_INTEGRANTE.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {otros.tipo === 'MUNICIPAL' && (
            <div>
              <Etiqueta>Localidad *</Etiqueta>
              {localidadFijaId != null ? (
                <>
                  <input
                    type="text"
                    value={localidadFijaNombre}
                    readOnly
                    className={`${inputBase} border-gray-200 bg-gray-100 text-gray-600`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Es la localidad de la convocatoria: un partido municipal de otra localidad no puede presentar
                    candidatos en esta elección.
                  </p>
                </>
              ) : (
                <select value={otros.localidadId} onChange={(e) => setOtros((o) => ({ ...o, localidadId: e.target.value }))} className={inputClass()}>
                  <option value="">Seleccione…</option>
                  {localidades.map((l) => (
                    <option key={l.id} value={l.id}>{l.nombre}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div className="flex items-end">
            <button type="button" onClick={agregarOtros} disabled={ocupado} className={botonSecundario}>
              Agregar como &quot;Otros&quot;
            </button>
          </div>
        </div>
      </Tarjeta>
    </>
  );
}

// =============================================================================
// PASO 2 — Identificación, domicilio y atributos
// =============================================================================

function Paso2Identificacion({
  datos,
  errores,
  guardar,
  mutar,
}: PropsPaso & { guardar: (cambios: Record<string, unknown>) => void }) {
  const { localidades } = useLocalidades();
  const a = datos.alianza;

  // Espejo local de los campos de texto: el PATCH con debounce contesta con la
  // alianza completa y sin este espejo el input perdería lo tipeado en el medio.
  const [campos, setCampos] = useState({
    denominacion: a.denominacion.startsWith('Alianza en formación') ? '' : a.denominacion,
    sigla: a.sigla || '',
    domicilioLegal: a.domicilioLegal || '',
    localidad: a.localidad || '',
    codigoPostal: a.codigoPostal || '',
    emailOficial: a.emailOficial || '',
    telefonoOficial: a.telefonoOficial || '',
    descripcionSimbolo: datos.borrador.atributos.descripcionSimbolo || '',
  });

  const [colorNuevo, setColorNuevo] = useState('#1d4ed8');
  // Los colores también van con espejo local: dos altas seguidas dentro de la
  // ventana del debounce leerían la lista del servidor todavía sin la primera y la
  // segunda la sobrescribiría. El espejo se inicializa una vez y esta pantalla es
  // la única que los toca.
  const [colores, setColores] = useState<string[]>(datos.borrador.atributos.colores);
  const simbolos = datos.documentos.filter((d) => d.tipo === 'SIMBOLO');

  const cambiar = (campo: keyof typeof campos, valor: string) => {
    setCampos((c) => ({ ...c, [campo]: valor }));
    guardar({ [campo]: valor });
  };

  const agregarColor = () => {
    const hex = colorNuevo.trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(hex)) {
      toast.error('El color debe estar en formato #RRGGBB');
      return;
    }
    if (colores.some((c) => c.toLowerCase() === hex)) {
      toast.error('Ese color ya está declarado');
      return;
    }
    if (colores.length >= MAX_COLORES) {
      toast.error(`Puede declarar hasta ${MAX_COLORES} colores`);
      return;
    }
    const nuevos = [...colores, hex];
    setColores(nuevos);
    guardar({ colores: nuevos });
  };

  const quitarColor = (hex: string) => {
    const nuevos = colores.filter((c) => c !== hex);
    setColores(nuevos);
    guardar({ colores: nuevos });
  };

  return (
    <>
      <Tarjeta titulo="Identificación de la alianza">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Etiqueta>Denominación *</Etiqueta>
            <input
              type="text"
              value={campos.denominacion}
              onChange={(e) => cambiar('denominacion', e.target.value)}
              className={inputClass(errores['denominacion'])}
              placeholder="Denominación con la que la alianza se presenta a la elección"
              maxLength={200}
            />
            <FieldError error={errores['denominacion']} />
          </div>
          <div>
            <Etiqueta>Sigla * (máx. 6 caracteres)</Etiqueta>
            <input
              type="text"
              value={campos.sigla}
              onChange={(e) => cambiar('sigla', e.target.value.toUpperCase().slice(0, 6))}
              className={`${inputClass(errores['sigla'])} font-mono uppercase`}
              maxLength={6}
            />
            <FieldError error={errores['sigla']} />
          </div>
          <div>
            <Etiqueta>Ámbito territorial</Etiqueta>
            <input type="text" value={tipoLabel(a.tipo)} readOnly className={`${inputBase} border-gray-200 bg-gray-100 text-gray-600`} />
            <p className="text-xs text-gray-500 mt-1">Derivado del integrante de mayor alcance. No se edita.</p>
          </div>
        </div>
      </Tarjeta>

      <Tarjeta titulo="Domicilio legal">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Etiqueta>Domicilio (calle, número, piso/depto) *</Etiqueta>
            <input type="text" value={campos.domicilioLegal} onChange={(e) => cambiar('domicilioLegal', e.target.value)} className={inputClass(errores['domicilioLegal'])} />
            <FieldError error={errores['domicilioLegal']} />
          </div>
          <div>
            <Etiqueta>Localidad *</Etiqueta>
            <select value={campos.localidad} onChange={(e) => cambiar('localidad', e.target.value)} className={inputClass(errores['localidad'])}>
              <option value="">Seleccione…</option>
              {localidades.map((l) => (
                <option key={l.id} value={l.nombre}>{l.nombre}</option>
              ))}
            </select>
            <FieldError error={errores['localidad']} />
          </div>
          <div>
            <Etiqueta>Código postal *</Etiqueta>
            <input type="text" value={campos.codigoPostal} onChange={(e) => cambiar('codigoPostal', e.target.value)} className={inputClass(errores['codigoPostal'])} maxLength={20} />
            <FieldError error={errores['codigoPostal']} />
          </div>
          <div>
            <Etiqueta>Correo institucional *</Etiqueta>
            <input type="email" value={campos.emailOficial} onChange={(e) => cambiar('emailOficial', e.target.value)} className={inputClass(errores['emailOficial'])} />
            <FieldError error={errores['emailOficial']} />
          </div>
          <div>
            <Etiqueta>Teléfono</Etiqueta>
            <input type="text" value={campos.telefonoOficial} onChange={(e) => cambiar('telefonoOficial', e.target.value)} className={inputClass()} maxLength={30} />
          </div>
        </div>
      </Tarjeta>

      <Tarjeta
        titulo="Símbolo, Emblema y Colores"
        subtitulo="Opcionales"
        accion={<span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">Opcional en esta etapa</span>}
      >
        <div className="space-y-5">
          <div>
            <Etiqueta>Colores (máx. {MAX_COLORES})</Etiqueta>
            <div className="flex flex-wrap items-center gap-2">
              <input type="color" value={colorNuevo} onChange={(e) => setColorNuevo(e.target.value)} className="w-12 h-10 border border-gray-300 rounded" />
              <input
                type="text"
                value={colorNuevo}
                onChange={(e) => setColorNuevo(e.target.value)}
                className={`${inputBase} border-gray-300 font-mono w-32`}
                maxLength={7}
                placeholder="#RRGGBB"
              />
              <button type="button" onClick={agregarColor} disabled={colores.length >= MAX_COLORES} className={botonSecundario}>
                Agregar color
              </button>
            </div>
            {colores.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {colores.map((hex) => (
                  <span key={hex} className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1">
                    <span className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: hex }} />
                    <span className="text-xs font-mono text-gray-700">{hex}</span>
                    <button type="button" onClick={() => quitarColor(hex)} className="text-red-500 text-xs">✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Etiqueta>Símbolo o Emblema (máx. {MAX_SIMBOLOS})</Etiqueta>
            <p className="text-xs text-gray-500 mb-2">
              <strong>Un símbolo por archivo</strong> (no combine varios en un mismo PDF). Imagen JPG o PNG, o PDF.
              Para reemplazar uno, quítelo y cargue el nuevo.
            </p>
            <BotonArchivo
              etiqueta="Cargar símbolo"
              disabled={simbolos.length >= MAX_SIMBOLOS}
              onArchivo={(archivo) => mutar(() => alianzaService.subirDocumento(datos.alianza.id, 'SIMBOLO', archivo), 'Símbolo cargado').then(() => undefined)}
            />
            {simbolos.length > 0 && (
              <ul className="mt-3 space-y-2">
                {simbolos.map((s) => (
                  <li key={s.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-700 truncate">{s.nombreOriginal}</span>
                    <button
                      type="button"
                      onClick={() => mutar(() => alianzaService.quitarDocumento(datos.alianza.id, s.id), 'Símbolo quitado')}
                      className="text-sm text-red-600 hover:text-red-700 ml-3"
                    >
                      Quitar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Etiqueta>Descripción del símbolo</Etiqueta>
            <textarea
              value={campos.descripcionSimbolo}
              onChange={(e) => cambiar('descripcionSimbolo', e.target.value)}
              rows={3}
              maxLength={500}
              className={`${inputBase} border-gray-300`}
              placeholder="Describa el emblema: colores, formas, elementos y su significado."
            />
          </div>
        </div>
      </Tarjeta>
    </>
  );
}

// =============================================================================
// PASO 3 — Documentación exigida por el Art. 27
// =============================================================================

function Paso3Documentacion({ datos, errores, mutar }: PropsPaso) {
  const porTipo = useMemo(() => {
    const mapa = new Map<string, DocumentoAlianza>();
    datos.documentos.forEach((d) => {
      if (!mapa.has(d.tipo)) mapa.set(d.tipo, d);
    });
    return mapa;
  }, [datos.documentos]);

  const actasFaltantes = datos.integrantes.filter((i) => !i.actaVoluntadDocumentoId).length;

  return (
    <>
      <div className="bg-white border border-gray-200 border-l-4 border-l-purple-700 p-4 rounded-r-lg mb-5 text-sm text-gray-700 shadow-sm">
        <p className="font-semibold text-purple-900 mb-1 uppercase tracking-wide text-xs">Requisito legal — Art. 27</p>
        <p>
          Todos los archivos deben estar firmados por el apoderado y por las autoridades partidarias competentes,
          según corresponda. Se admite PDF o imagen.
        </p>
      </div>

      <Tarjeta
        titulo="Constancia de la voluntad de aliarse de cada partido integrante"
        subtitulo="Art. 27 inc. a) · un archivo por cada partido integrante"
        accion={
          <span className={`text-xs px-2 py-1 rounded-full border ${actasFaltantes === 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
            {actasFaltantes === 0 ? 'Completo' : `Faltan ${actasFaltantes}`}
          </span>
        }
      >
        <p className="text-sm text-gray-500 italic mb-4">
          Acta de asamblea, de congreso o de reunión del consejo directivo —según lo que establezca la Carta Orgánica
          de cada partido— donde se aprueba la voluntad de constituir esta alianza.
        </p>

        {datos.integrantes.length === 0 ? (
          <p className="text-sm text-gray-500">Agregue los partidos integrantes en el paso 1 para cargar sus actas.</p>
        ) : (
          <ul className="space-y-2">
            {datos.integrantes.map((i) => {
              const nombre = i.miembro?.denominacion || i.miembroNoMigradoDenominacion || 'Sin denominación';
              const cargada = !!i.actaVoluntadDocumentoId;
              return (
                <li
                  key={i.id}
                  className={`flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg border ${cargada ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                >
                  <Marca ok={cargada} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{nombre}</p>
                    <p className="text-xs text-gray-600">
                      {cargada
                        ? `${i.actaVoluntadDocumento?.nombreOriginal} · versión ${i.actaVoluntadDocumento?.version}`
                        : 'Pendiente de carga · obligatorio'}
                    </p>
                  </div>
                  <BotonArchivo
                    etiqueta={cargada ? 'Reemplazar' : 'Cargar'}
                    onArchivo={(archivo) =>
                      mutar(() => alianzaService.cargarActaVoluntad(datos.alianza.id, i.id, archivo), 'Acta cargada').then(() => undefined)
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        <FieldError error={errores['actas']} />
      </Tarjeta>

      <Tarjeta titulo="Documentación de la alianza" subtitulo="Art. 27 incs. c), d) y e)">
        <ul className="space-y-3">
          {DOCUMENTOS_ART_27.map((doc) => {
            const cargado = porTipo.get(doc.tipo);
            return (
              <li
                key={doc.tipo}
                className={`flex flex-wrap items-start gap-3 px-4 py-3 rounded-lg border ${cargado ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
              >
                <Marca ok={!!cargado} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{doc.titulo}</p>
                  <p className="text-xs text-gray-500">{doc.articulo}</p>
                  <p className="text-xs text-gray-500 italic mt-1">{doc.explicacion}</p>
                  <p className="text-xs mt-1 text-gray-700">
                    {cargado ? `${cargado.nombreOriginal} · versión ${cargado.version}` : 'Pendiente de carga'}
                  </p>
                </div>
                <BotonArchivo
                  etiqueta={cargado ? 'Reemplazar' : 'Cargar'}
                  onArchivo={(archivo) =>
                    mutar(() => alianzaService.subirDocumento(datos.alianza.id, doc.tipo, archivo), 'Documento cargado').then(() => undefined)
                  }
                />
              </li>
            );
          })}
        </ul>
        <FieldError error={errores['documentos']} />
      </Tarjeta>

    </>
  );
}

// =============================================================================
// PASO 4 — Apoderados y acceso al sistema
// =============================================================================
// ESTRUCTURA: UN SOLO FORMULARIO, TODO VISIBLE DE ENTRADA
// ------------------------------------------------------
// Mismo criterio que el paso 1 de /constitucion: los datos personales, la cuenta
// de acceso y la documentación se ven desde el primer momento, en un formulario
// plano. Los archivos se eligen antes de guardar y se suben cuando se guarda, así
// que no hay dos etapas ("primero creá la persona, después subí los papeles").
//
// NO SE CONSULTA NADA SOBRE LAS PERSONAS
// --------------------------------------
// Ni del apoderado ni de las autoridades de los órganos: nada se precarga, no se
// consulta el padrón y no se informa si ya tienen documentos en el sistema. Todos
// pueden ser de cualquier jurisdicción, sus datos los escribe quien completa el
// formulario, y el frente y el dorso del DNI se piden siempre en este expediente.
// La única validación de identidad es que una persona no integre dos veces el
// mismo órgano.

/** Géneros que la persona elige a mano. Mismo listado que /constitucion. */
const GENEROS = [
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'OTRO', label: 'Otro' },
];

interface CamposApoderado {
  dni: string;
  apellido: string;
  nombre: string;
  genero: string;
  fechaNacimiento: string;
  telefono: string;
  email: string;
  partidoIntegranteId: string;
}

const camposApoderadoVacios = (): CamposApoderado => ({
  dni: '', apellido: '', nombre: '', genero: '', fechaNacimiento: '',
  telefono: '', email: '', partidoIntegranteId: '',
});

/** Un archivo elegido pero todavía no subido. */
type ArchivoElegido = File | null;

interface ArchivosApoderado {
  acta: ArchivoElegido;
  dniFrente: ArchivoElegido;
  dniDorso: ArchivoElegido;
}

const archivosVacios = (): ArchivosApoderado => ({ acta: null, dniFrente: null, dniDorso: null });

/** Input de archivo plano, con el nombre del elegido debajo. Igual que /constitucion. */
function CampoArchivo({
  etiqueta,
  archivo,
  cargado,
  onElegir,
  error,
}: {
  etiqueta: string;
  archivo: ArchivoElegido;
  /** Documento ya guardado en el expediente, si hay. */
  cargado?: DocumentoAlianza;
  onElegir: (file: File | null) => void;
  error?: string;
}) {
  return (
    <div className={error ? 'bg-red-50 rounded p-2' : ''}>
      <Etiqueta>{etiqueta}</Etiqueta>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => onElegir(e.target.files?.[0] || null)}
        className="w-full text-sm"
      />
      {archivo
        ? <p className="text-xs text-green-700 mt-1">✓ {archivo.name} (se sube al guardar)</p>
        : cargado
          ? <p className="text-xs text-green-700 mt-1">✓ {cargado.nombreOriginal} · versión {cargado.version}</p>
          : <p className="text-xs text-gray-500 mt-1">Sin archivo. PDF o imagen.</p>}
      <FieldError error={error} />
    </div>
  );
}

/** Archivo ya guardado de una persona cargada: se ve, no se reemplaza. */
function ArchivoGuardado({ etiqueta, documento }: { etiqueta: string; documento?: DocumentoAlianza }) {
  return (
    <div>
      <Etiqueta>{etiqueta}</Etiqueta>
      {documento
        ? <p className="text-xs text-green-700">✓ {documento.nombreOriginal} · versión {documento.version}</p>
        : <p className="text-xs text-red-600">Pendiente</p>}
    </div>
  );
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export interface PersonaPadron {
  apellido: string;
  nombre: string;
  genero: string;
  /** La "clase": año de nacimiento. Es lo único que el padrón sabe de la fecha. */
  anioNacimiento?: string;
}

/**
 * Datos de la persona en el padrón electoral provincial, por DNI.
 *
 * El padrón NO es un requisito: una autoridad o un apoderado de alianza puede ser de
 * otra jurisdicción. Si no está, `persona` queda en null y los campos se completan a
 * mano. Si está, sus datos filiatorios mandan — el backend los vuelve a aplicar al
 * guardar, así que la pantalla no puede quedar diciendo otra cosa.
 */
function usePersonaPadron() {
  const [estado, setEstado] = useState<{ consultando: boolean; persona: PersonaPadron | null; consultado: boolean }>({
    consultando: false, persona: null, consultado: false,
  });

  const consultar = useCallback(async (dni: string) => {
    const limpio = (dni || '').replace(/\D/g, '');
    if (limpio.length < 7) {
      setEstado({ consultando: false, persona: null, consultado: false });
      return null;
    }
    setEstado({ consultando: true, persona: null, consultado: false });
    try {
      const r = await padronService.validarDni(limpio);
      const res = r.data.data;
      if (!res?.valido || !res.datos) {
        setEstado({ consultando: false, persona: null, consultado: true });
        return null;
      }
      const persona: PersonaPadron = {
        apellido: res.datos.apellido || '',
        nombre: res.datos.nombre || '',
        genero: res.datos.genero || '',
        anioNacimiento: res.datos.anio_nacimiento ? String(res.datos.anio_nacimiento) : undefined,
      };
      setEstado({ consultando: false, persona, consultado: true });
      return persona;
    } catch {
      // Si la consulta falla no se bloquea la carga: se completa a mano y el
      // backend vuelve a intentarlo al guardar.
      setEstado({ consultando: false, persona: null, consultado: true });
      return null;
    }
  }, []);

  const limpiar = useCallback(() => setEstado({ consultando: false, persona: null, consultado: false }), []);

  return { ...estado, consultar, limpiar };
}

/** Parte un ISO `YYYY-MM-DD` en sus tres pedazos, sin ceros a la izquierda. */
function partirFecha(iso?: string) {
  const [a = '', m = '', d = ''] = (iso || '').split('-');
  return { dia: d ? String(Number(d)) : '', mes: m ? String(Number(m)) : '', anio: a };
}

/** Arma el ISO sólo cuando están los tres pedazos; si falta uno, ''. */
function componerFecha(dia: string, mes: string, anio: string) {
  if (!dia || !mes || anio.length !== 4) return '';
  return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
}

/** Días que tiene ese mes (con año, para febrero). 31 mientras no haya mes. */
function diasDelMes(mes: string, anio: string) {
  if (!mes) return 31;
  const m = Number(mes);
  if (m === 2) {
    const a = Number(anio);
    const bisiesto = !!a && (a % 4 === 0 && (a % 100 !== 0 || a % 400 === 0));
    return bisiesto ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

/**
 * Fecha de nacimiento en tres partes. El AÑO lo fija el padrón cuando la persona
 * está empadronada (es el único dato de la fecha que tenemos), y sólo se piden el
 * día y el mes. Sin padrón, los tres campos se completan a mano.
 *
 * No se usa `<input type="date">` porque no admite un año fijo: mostraba una fecha
 * entera editable y el año tipeado se descartaba en silencio al guardar.
 *
 * ESTADO PROPIO, NO DERIVADO DE `valor`: un ISO `YYYY-MM-DD` no puede representar
 * una fecha a medio completar. Cuando los tres pedazos se leían de `valor`, elegir
 * el día emitía '' (todavía faltaban mes y año), el padre guardaba '' y el select
 * se volvía a dibujar vacío: no se podía elegir nada. Los pedazos viven acá y
 * `onCambiar` recibe el ISO sólo cuando la fecha está completa.
 */
function CampoFechaNacimiento({
  valor,
  onCambiar,
  anioPadron,
  chico,
  error,
}: {
  /** ISO `YYYY-MM-DD`, o '' si la fecha todavía no está completa. */
  valor: string;
  onCambiar: (iso: string) => void;
  /** Año del padrón: si viene, el campo del año queda fijo. */
  anioPadron?: string;
  chico?: boolean;
  error?: string;
}) {
  const [partes, setPartes] = useState(() => partirFecha(valor));
  const anioEfectivo = anioPadron || partes.anio;

  // El valor puede cambiar DESDE AFUERA: el padrón reescribe el año al resolver el
  // DNI. Se adopta sólo si difiere de lo que este campo compondría, para no pisar
  // una selección a medio hacer (con fecha incompleta ambos son '' y no pasa nada).
  useEffect(() => {
    if ((valor || '') !== componerFecha(partes.dia, partes.mes, anioEfectivo)) {
      setPartes(partirFecha(valor));
    }
  }, [valor]);

  // Si el año del padrón llega después de que ya se eligieron día y mes, la fecha
  // recién ahí queda completa y hay que avisarle al padre.
  useEffect(() => {
    if (anioPadron && partes.dia && partes.mes) {
      onCambiar(componerFecha(partes.dia, partes.mes, anioPadron));
    }
  }, [anioPadron]);

  /** Guarda el pedazo y emite el ISO (o '' mientras esté incompleta). */
  const cambiarParte = (parte: 'dia' | 'mes' | 'anio', bruto: string) => {
    const siguiente = { ...partes, [parte]: bruto };
    // Un 31 elegido antes del mes no puede quedar en un mes de 30.
    const tope = diasDelMes(siguiente.mes, anioPadron || siguiente.anio);
    if (Number(siguiente.dia) > tope) siguiente.dia = String(tope);
    setPartes(siguiente);
    onCambiar(componerFecha(siguiente.dia, siguiente.mes, anioPadron || siguiente.anio));
  };

  const clase = chico
    ? `w-full px-2 py-1.5 border rounded text-sm ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'}`
    : inputClass(error);
  const claseFija = chico
    ? 'w-full px-2 py-1.5 border border-gray-200 bg-gray-100 text-gray-500 rounded text-sm cursor-not-allowed'
    : inputBloqueado;

  return (
    <div className="grid grid-cols-3 gap-2">
      <select value={partes.dia} onChange={(e) => cambiarParte('dia', e.target.value)} className={clase}>
        <option value="">Día</option>
        {Array.from({ length: diasDelMes(partes.mes, anioEfectivo) }, (_, i) => String(i + 1)).map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
      <select value={partes.mes} onChange={(e) => cambiarParte('mes', e.target.value)} className={clase}>
        <option value="">Mes</option>
        {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
      </select>
      {anioPadron ? (
        <input type="text" value={anioPadron} disabled title="Año de nacimiento según el padrón" className={claseFija} />
      ) : (
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={partes.anio}
          onChange={(e) => cambiarParte('anio', e.target.value.replace(/\D/g, ''))}
          placeholder="Año"
          className={clase}
        />
      )}
    </div>
  );
}

function Paso4Apoderados({
  datos,
  errores,
  mutar,
  credenciales,
  setCredenciales,
}: PropsPaso & {
  credenciales: { email: string; password: string; passwordConfirmacion: string };
  setCredenciales: React.Dispatch<React.SetStateAction<{ email: string; password: string; passwordConfirmacion: string }>>;
}) {
  const [mostrarNuevo, setMostrarNuevo] = useState(datos.apoderados.length === 0);

  const titularId = datos.borrador.apoderadoAccesoId || datos.apoderados[0]?.id;

  const nombreIntegrante = (id?: string) => {
    if (!id) return null;
    const i = datos.integrantes.find((x) => x.id === id);
    return i ? (i.miembro?.denominacion || i.miembroNoMigradoDenominacion) : null;
  };

  return (
    <>
      <div className="bg-white border border-gray-200 border-l-4 border-l-purple-700 p-4 rounded-r-lg mb-5 text-sm text-purple-900 shadow-sm">
        <p className="mt-2">
          Todos los apoderados que cargue quedan registrados como <strong>iniciadores del trámite</strong>. La cuenta
          de acceso al sistema se crea para uno; a los demás se les puede crear usuario después, desde el sistema.
        </p>
      </div>

      {/* La cuenta de acceso va DENTRO de la tarjeta del apoderado iniciador: es
          su cuenta, no una configuración de la alianza. Ver SeccionCuentaAcceso. */}
      {datos.apoderados.map((ap) => (
        <TarjetaApoderado
          key={ap.id}
          apoderado={ap}
          datos={datos}
          mutar={mutar}
          esTitular={ap.id === titularId}
          designante={nombreIntegrante(datos.borrador.designantes.apoderados[ap.id])}
          errores={errores}
          credenciales={credenciales}
          setCredenciales={setCredenciales}
        />
      ))}

      <FieldError error={errores['apoderados']} />

      {mostrarNuevo && (
        <FormularioApoderado
          datos={datos}
          mutar={mutar}
          esPrimero={datos.apoderados.length === 0}
          errores={errores}
          credenciales={credenciales}
          setCredenciales={setCredenciales}
          onListo={() => setMostrarNuevo(false)}
          onCancelar={datos.apoderados.length > 0 ? () => setMostrarNuevo(false) : undefined}
        />
      )}

      {/* Alta de otro apoderado: siempre visible una vez que hay uno cargado, para
          que no haya que adivinar dónde se agregan los demás. */}
      {(datos.apoderados.length > 0 || !mostrarNuevo) && (
        <button
          type="button"
          onClick={() => setMostrarNuevo(true)}
          disabled={mostrarNuevo}
          className="w-full py-3 bg-white border-2 border-dashed border-purple-300 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-50 disabled:opacity-50 disabled:hover:bg-white transition"
        >
          + Agregar otro apoderado
        </button>
      )}
    </>
  );
}



/**
 * Cuenta de acceso del apoderado iniciador. Se renderiza DENTRO de su tarjeta (la
 * de alta o la del apoderado ya guardado): es la cuenta de ESA persona, no una
 * configuración de la alianza. El correo es también el correo del apoderado (el
 * sistema sincroniza usuario↔apoderado por DNI), así que se carga una sola vez y
 * se copia a su fila.
 *
 * La contraseña no se persiste en el borrador: viaja recién en el envío.
 */
/**
 * Chequeo en vivo de disponibilidad del correo como cuenta de acceso.
 *
 * Existe porque el choque de correos se descubría recién al enviar el trámite
 * entero, y en un caso real terminó peor: el correo estaba registrado a nombre de
 * otra persona y el envío tomó a ESE usuario como firmante del expediente. Ahora
 * el formulario lo avisa mientras se escribe y el envío lo rechaza.
 */
function useCorreoDisponible() {
  const [estado, setEstado] = useState<{
    verificando: boolean;
    disponible?: boolean;
    esLaMismaPersona?: boolean;
    mensaje?: string;
  }>({ verificando: false });

  const verificar = useCallback(async (email: string, dni?: string) => {
    const limpio = email.trim().toLowerCase();
    if (!limpio || !EMAIL_RE.test(limpio)) {
      setEstado({ verificando: false });
      return;
    }
    setEstado({ verificando: true });
    try {
      const r = await alianzaService.correoDisponible(limpio, dni);
      setEstado({ verificando: false, ...r.data.data });
    } catch {
      // Si la consulta falla no se bloquea nada: el envío vuelve a validarlo.
      setEstado({ verificando: false });
    }
  }, []);

  const limpiar = useCallback(() => setEstado({ verificando: false }), []);

  return { estado, verificar, limpiar };
}

/**
 * ¿La persona de este DNI ya tiene usuario en SIREPPAR?
 *
 * Si lo tiene, el envío reutiliza esa cuenta (busca por DNI, no por correo), así que
 * pedirle correo y contraseña nuevos sería mentirle: la contraseña se descartaba y
 * seguía entrando con la vieja. Con esto los campos se reemplazan por un aviso.
 *
 * El correo viene ENMASCARADO (`fso•••••@dominio`): esta pantalla es pública y sin
 * sesión, así que con un DNI cualquiera se puede preguntar. Alcanza para que el
 * titular reconozca su casilla y no alcanza para que un tercero la escriba.
 */
interface AccesoExistente {
  consultando: boolean;
  tieneUsuario?: boolean;
  /** Parcialmente oculto: el backend NUNCA manda el correo completo. */
  emailMascarado?: string;
}

function useAccesoExistente(dni?: string) {
  const [estado, setEstado] = useState<AccesoExistente>({ consultando: false });

  const limpio = (dni || '').replace(/\D/g, '');

  useEffect(() => {
    if (limpio.length < 7) {
      setEstado({ consultando: false });
      return;
    }
    let vigente = true;
    setEstado({ consultando: true });
    alianzaService.accesoPorDni(limpio)
      .then((r) => { if (vigente) setEstado({ consultando: false, ...r.data.data }); })
      // Si la consulta falla no se bloquea nada: se piden las credenciales y el envío
      // vuelve a decidir por DNI.
      .catch(() => { if (vigente) setEstado({ consultando: false }); });
    return () => { vigente = false; };
  }, [limpio]);

  return estado;
}

/** Aviso del chequeo de correo, debajo del campo. */
function AvisoCorreo({ estado }: { estado: ReturnType<typeof useCorreoDisponible>['estado'] }) {
  if (estado.verificando) return <p className="text-xs text-gray-500 mt-1">Verificando el correo…</p>;
  if (estado.disponible === undefined) return null;
  if (estado.disponible) {
    return estado.esLaMismaPersona
      ? <p className="text-xs text-green-700 mt-1">✓ {estado.mensaje}</p>
      : <p className="text-xs text-green-700 mt-1">✓ Correo disponible.</p>;
  }
  return <p className="text-xs text-red-600 mt-1">⚠️ {estado.mensaje}</p>;
}

function SeccionCuentaAcceso({
  titular,
  dni,
  acceso,
  mutar,
  errores,
  errorEmailLocal,
  credenciales,
  setCredenciales,
}: {
  /** Apoderado iniciador ya guardado, si existe: su correo se sincroniza con éste. */
  titular?: ApoderadoAlianza;
  /** DNI de la persona, para el chequeo de disponibilidad del correo. */
  dni?: string;
  /**
   * Lo consulta el que llama, no esta sección: quien valida el formulario necesita
   * saber si la persona ya tiene usuario (si lo tiene, acá no se pide correo y
   * exigirlo trababa el alta con un error sin campo donde mostrarse).
   */
  acceso: AccesoExistente;
  mutar: Mutar;
  errores: Record<string, string>;
  /** Error del formulario de alta (no del paso), cuando aplica. */
  errorEmailLocal?: string;
  credenciales: { email: string; password: string; passwordConfirmacion: string };
  setCredenciales: React.Dispatch<React.SetStateAction<{ email: string; password: string; passwordConfirmacion: string }>>;
}) {
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const { estado: estadoCorreo, verificar, limpiar } = useCorreoDisponible();

  /** Verifica disponibilidad y copia el correo a la fila del apoderado titular. */
  const alSalirDelCorreo = () => {
    const nuevo = credenciales.email.trim().toLowerCase();
    verificar(nuevo, titular?.dni || dni);
    if (!titular) return;
    if (!nuevo || !EMAIL_RE.test(nuevo) || nuevo === (titular.email || '')) return;
    mutar(() => alianzaService.actualizarApoderado(titular.id, { email: nuevo }));
  };

  // Ya tiene usuario: no se le pide nada, se le dice con qué cuenta va a entrar.
  if (acceso.tieneUsuario) {
    return (
      <div className="border-t mt-6 pt-6">
        <h3 className="font-semibold text-gray-900">Cuenta de acceso al sistema</h3>
        <p className="text-sm text-gray-500 mb-4">
          Esta persona ya tiene usuario en SIREPPAR: entra a la alianza con esa misma cuenta.
        </p>
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3">
          <div>
            <Etiqueta>Correo de acceso</Etiqueta>
            {/* Enmascarado y como TEXTO, no como <input>: no hay nada que editar y
                un campo real invitaría al navegador a autocompletarlo. */}
            <p className="rounded-lg border border-green-300 bg-white px-3 py-2 font-mono text-gray-700 break-all">
              {acceso.emailMascarado || '•••••'}
            </p>
            <p className="text-xs text-green-800 mt-1">
              Es su correo registrado en el sistema. Ahí van a llegar las notificaciones del trámite.
            </p>
          </div>
          <div>
            <Etiqueta>Contraseña</Etiqueta>
            {/* Nunca un input de contraseña, ni de sólo lectura con un valor falso:
                los gestores de contraseñas los rellenan y los leen. Acá no hay
                contraseña que mostrar — el backend no la manda ni podría. */}
            <p className="rounded-lg border border-green-300 bg-white px-3 py-2 text-gray-500">
              ••••••••••
            </p>
            <p className="text-xs text-green-800 mt-1">
              <strong>La misma de su usuario en SIREPPAR.</strong> No hace falta definir una nueva.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (acceso.consultando) {
    return (
      <div className="border-t mt-6 pt-6">
        <h3 className="font-semibold text-gray-900">Cuenta de acceso al sistema</h3>
        <p className="text-sm text-gray-500 mt-2">Verificando si esta persona ya tiene usuario…</p>
      </div>
    );
  }

  return (
    <div className="border-t mt-6 pt-6">
      <h3 className="font-semibold text-gray-900">Cuenta de acceso al sistema</h3>
      <p className="text-sm text-gray-500 mb-4">
        Es la cuenta de este apoderado: con estos datos va a seguir el expediente después de enviarlo.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Etiqueta>Correo de acceso *</Etiqueta>
          <input
            type="email"
            value={credenciales.email}
            onChange={(e) => { setCredenciales((c) => ({ ...c, email: e.target.value })); limpiar(); }}
            onBlur={alSalirDelCorreo}
            className={inputClass(errores['acceso.email'] || errorEmailLocal || (estadoCorreo.disponible === false ? 'x' : undefined))}
            placeholder="usuario@ejemplo.com"
          />
          <AvisoCorreo estado={estadoCorreo} />
          <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 space-y-1">
            <p> Este correo será el <strong>usuario de acceso</strong> al sistema SIREPPAR y el correo del apoderado iniciador. Además queda constituido como <strong>domicilio electrónico</strong> de la alianza: a esa casilla
              llegan las notificaciones y observaciones del trámite.</p>
          </div>
          <FieldError error={errores['acceso.email'] || errorEmailLocal} />
        </div>

        <div>
          <Etiqueta>Contraseña *</Etiqueta>
          <div className="relative">
            <input
              type={mostrarPassword ? 'text' : 'password'}
              value={credenciales.password}
              onChange={(e) => setCredenciales((c) => ({ ...c, password: e.target.value }))}
              className={inputClass(errores['acceso.password'])}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setMostrarPassword(!mostrarPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {mostrarPassword ? '◉' : '○'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Mínimo 8 caracteres, con letras y números.</p>
          <FieldError error={errores['acceso.password']} />
        </div>

        <div>
          <Etiqueta>Repetir contraseña *</Etiqueta>
          <div className="relative">
            <input
              type={mostrarConfirmacion ? 'text' : 'password'}
              value={credenciales.passwordConfirmacion}
              onChange={(e) => setCredenciales((c) => ({ ...c, passwordConfirmacion: e.target.value }))}
              className={inputClass(errores['acceso.passwordConfirmacion'])}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setMostrarConfirmacion(!mostrarConfirmacion)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {mostrarConfirmacion ? '◉' : '○'}
            </button>
          </div>
          <FieldError error={errores['acceso.passwordConfirmacion']} />
        </div>
      </div>
    </div>
  );
}

/**
 * Un apoderado ya guardado. Mismos campos que el formulario de alta, pero GRISES Y
 * DESHABILITADOS: una vez guardada, la persona no se corrige en su lugar — se Quita
 * y se vuelve a cargar.
 *
 * POR QUÉ: el DNI es lo que vincula a la persona con sus archivos
 * (`Documento.dniPersona`), y cada campo se guardaba solo al salir de él. Corregir
 * un DNI ahí dejaba el frente/dorso y el acta colgando del número viejo, con el
 * checklist dándolos por cargados. Quitar y volver a cargar es un camino único y
 * explícito: el backend da de baja esos archivos al quitar (ver
 * `quitarApoderado`), así que la persona nueva arranca sin heredar nada.
 *
 * La DOCUMENTACIÓN sí se puede reemplazar: cambiar una foto ilegible no cambia de
 * persona, y el versionado del documento ya lo cubre.
 */
function TarjetaApoderado({
  apoderado,
  datos,
  mutar,
  esTitular,
  designante,
  errores,
  credenciales,
  setCredenciales,
}: {
  apoderado: ApoderadoAlianza;
  datos: AlianzaCompleta;
  mutar: Mutar;
  esTitular: boolean;
  designante: string | null;
  errores: Record<string, string>;
  credenciales: { email: string; password: string; passwordConfirmacion: string };
  setCredenciales: React.Dispatch<React.SetStateAction<{ email: string; password: string; passwordConfirmacion: string }>>;
}) {
  const guardado = datos.borrador.personasApoderado?.[apoderado.id];
  const designanteGuardado = datos.borrador.designantes.apoderados[apoderado.id] || '';
  const acceso = useAccesoExistente(apoderado.dni);

  const docDe = (tipo: string) =>
    datos.documentos.find((d) => d.tipo === tipo && d.dniPersona === apoderado.dni);

  return (
    <section className="relative bg-white rounded-xl border border-gray-200 border-l-4 border-l-purple-700 mb-5">
      <button
        type="button"
        onClick={() => mutar(() => alianzaService.quitarApoderado(apoderado.id), 'Apoderado quitado')}
        className="absolute top-3 right-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
      >
        Quitar
      </button>

      <div className="px-5 py-4 pr-28 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900">{apoderado.apellido}, {apoderado.nombre}</h2>
          <p className="text-sm text-gray-500">
            DNI {apoderado.dni}{designante ? ` · designado por ${designante}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {esTitular ? (
            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
              Titular del acceso al sistema
            </span>
          ) : (
            <button
              type="button"
              onClick={() => mutar(() => alianzaService.designarApoderadoDeAcceso(apoderado.id), 'Titular del acceso actualizado')}
              className="text-xs text-purple-700 hover:text-purple-800"
            >
              Hacer titular del acceso
            </button>
          )}
        </div>
      </div>

      <div className="p-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Etiqueta>DNI *</Etiqueta>
            <input type="text" value={apoderado.dni} disabled className={inputBloqueado} />
          </div>
          <div>
            <Etiqueta>Teléfono</Etiqueta>
            <input type="tel" value={apoderado.telefono || '—'} disabled className={inputBloqueado} />
          </div>
          <div>
            <Etiqueta>Apellido *</Etiqueta>
            <input type="text" value={apoderado.apellido} disabled className={inputBloqueado} />
          </div>
          <div>
            <Etiqueta>Nombre *</Etiqueta>
            <input type="text" value={apoderado.nombre} disabled className={inputBloqueado} />
          </div>
          <div>
            <Etiqueta>Género *</Etiqueta>
            <select value={apoderado.genero || ''} disabled className={inputBloqueado}>
              <option value="">Sin especificar</option>
              {GENEROS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <Etiqueta>Fecha de nacimiento *</Etiqueta>
            <input type="date" value={guardado?.fechaNacimiento || ''} disabled className={inputBloqueado} />
          </div>
          {/* El titular NO tiene campo de correo: el suyo es el de la cuenta de
              acceso, que se carga en la tarjeta de abajo. Un usuario es una persona
              y el sistema sincroniza usuario↔apoderado por DNI, así que mostrar dos
              campos —aunque uno fuera de solo lectura— invita a la divergencia.
              Los demás apoderados sí llevan su correo, porque no tienen cuenta. */}
          {!esTitular && (
            <div>
              <Etiqueta>Correo</Etiqueta>
              <input
                type="email"
                value={acceso.tieneUsuario ? (acceso.emailMascarado || '•••••') : (apoderado.email || '—')}
                disabled
                className={inputBloqueado}
              />
              <p className="text-xs text-gray-500 mt-1">
                {acceso.tieneUsuario
                  // La leyenda de "podrán crear usuarios" es falsa para quien ya tiene
                  // uno: no se le crea ninguno, se le da acceso a la alianza con el suyo.
                  ? 'Ya tiene usuario en SIREPPAR: con esa cuenta va a poder administrar la alianza.'
                  : 'Dentro del sistema podrán crearle un usuario.'}
              </p>
            </div>
          )}
          <div>
            <Etiqueta>Partido que lo designa</Etiqueta>
            <select value={designanteGuardado} disabled className={inputBloqueado}>
              <option value="">Sin especificar</option>
              {datos.integrantes.map((i) => (
                <option key={i.id} value={i.id}>{i.miembro?.denominacion || i.miembroNoMigradoDenominacion}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="border-t mt-6 pt-6">
          <h3 className="font-semibold text-gray-900 mb-4">Documentación</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <ArchivoGuardado etiqueta="Acta de designación *" documento={docDe('ACTA_DESIGNACION_APODERADOS')} />
            <ArchivoGuardado etiqueta="📷 DNI Frente *" documento={docDe('DNI_FRENTE_APODERADO')} />
            <ArchivoGuardado etiqueta="📷 DNI Dorso *" documento={docDe('DNI_DORSO_APODERADO')} />
          </div>
        </div>

        {esTitular && (
          <SeccionCuentaAcceso
            titular={apoderado}
            acceso={acceso}
            mutar={mutar}
            errores={errores}
            credenciales={credenciales}
            setCredenciales={setCredenciales}
          />
        )}
      </div>
    </section>
  );
}

/**
 * Alta de un apoderado en una sola pasada: datos personales y los tres archivos.
 * Al guardar crea la persona y sube los archivos elegidos, así que quien completa
 * el formulario no tiene que volver sobre la tarjeta para adjuntar nada.
 */
function FormularioApoderado({
  datos,
  mutar,
  esPrimero,
  errores: erroresPaso,
  credenciales,
  setCredenciales,
  onListo,
  onCancelar,
}: {
  datos: AlianzaCompleta;
  mutar: Mutar;
  esPrimero: boolean;
  errores: Record<string, string>;
  /** Cuenta de acceso del iniciador: se pide en esta misma tarjeta. */
  credenciales: { email: string; password: string; passwordConfirmacion: string };
  setCredenciales: React.Dispatch<React.SetStateAction<{ email: string; password: string; passwordConfirmacion: string }>>;
  onListo: () => void;
  onCancelar?: () => void;
}) {
  const emailAcceso = credenciales.email;
  const [campos, setCampos] = useState<CamposApoderado>(camposApoderadoVacios());
  const [archivos, setArchivos] = useState<ArchivosApoderado>(archivosVacios());
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [progreso, setProgreso] = useState('');
  // Los apoderados adicionales no reciben cuenta ahora, pero se les crea usuario
  // desde el sistema más adelante: si el correo ya es de otra persona, ese alta va
  // a chocar. Mejor avisarlo acá que dentro de tres meses.
  const { estado: estadoCorreo, verificar, limpiar } = useCorreoDisponible();
  const padron = usePersonaPadron();
  const acceso = useAccesoExistente(campos.dni);

  const cambiar = (campo: keyof CamposApoderado, valor: string) => {
    setCampos((c) => ({ ...c, [campo]: valor }));
    if (errores[campo]) setErrores((e) => { const n = { ...e }; delete n[campo]; return n; });
  };

  /** Al salir del DNI: si la persona está en el padrón, sus datos mandan. */
  const alSalirDelDni = async () => {
    const persona = await padron.consultar(campos.dni);
    if (!persona) return;
    setCampos((c) => ({
      ...c,
      apellido: persona.apellido,
      nombre: persona.nombre,
      genero: persona.genero || c.genero,
      // Se conservan el día y el mes ya elegidos; el año lo pone el padrón.
      fechaNacimiento: persona.anioNacimiento && c.fechaNacimiento
        ? `${persona.anioNacimiento}-${c.fechaNacimiento.slice(5)}`
        : c.fechaNacimiento,
    }));
    setErrores((e) => { const n = { ...e }; delete n.apellido; delete n.nombre; delete n.genero; return n; });
  };

  const validar = (): boolean => {
    const err: Record<string, string> = {};
    if (!campos.dni || campos.dni.length < 7 || campos.dni.length > 8) err.dni = 'El DNI debe tener 7 u 8 dígitos';
    if (!campos.apellido.trim()) err.apellido = 'El apellido es obligatorio';
    if (!campos.nombre.trim()) err.nombre = 'El nombre es obligatorio';
    if (!campos.genero) err.genero = 'El género es obligatorio';
    if (!campos.fechaNacimiento) err.fechaNacimiento = 'La fecha de nacimiento es obligatoria';
    // El iniciador no tiene campo de correo propio: usa el de la cuenta de acceso,
    // que tiene que estar cargado antes de guardarlo.
    //
    // SALVO que la persona ya tenga usuario en SIREPPAR: en ese caso la sección de
    // acceso no muestra ningún campo de correo —sólo el enmascarado, como texto— y
    // el correo lo resuelve el backend por DNI al enviar. Exigirlo igual dejaba el
    // alta trabada con "Revise los campos marcados en rojo" y NADA en rojo, porque
    // el error no tenía campo donde pintarse.
    if (esPrimero && !acceso.tieneUsuario && (!emailAcceso || !EMAIL_RE.test(emailAcceso))) {
      err.emailAcceso = 'El correo de acceso es obligatorio: es el correo del apoderado iniciador';
    }
    // Con usuario existente no hay campo de correo: nada que validar (mismo criterio
    // que el iniciador, arriba).
    const pideCorreo = !esPrimero && !acceso.tieneUsuario;
    if (pideCorreo && campos.email && !EMAIL_RE.test(campos.email)) err.email = 'El correo no es válido';
    else if (pideCorreo && estadoCorreo.disponible === false) err.email = estadoCorreo.mensaje || 'Ese correo ya está en uso';
    if (!archivos.acta) err.acta = 'El acta de designación es obligatoria';
    if (!archivos.dniFrente) err.dniFrente = 'El frente del DNI es obligatorio';
    if (!archivos.dniDorso) err.dniDorso = 'El dorso del DNI es obligatorio';
    setErrores(err);
    return Object.keys(err).length === 0;
  };

  const guardar = async () => {
    if (!validar()) {
      toast.error('Revise los campos marcados en rojo');
      return;
    }
    setGuardando(true);
    try {
      // 1. Los archivos primero: si el almacenamiento falla, no queda una persona
      //    a medio cargar sin sus papeles.
      setProgreso('Subiendo archivos…');
      const [acta, frente, dorso] = await Promise.all([
        subirArchivo(archivos.acta as File),
        subirArchivo(archivos.dniFrente as File),
        subirArchivo(archivos.dniDorso as File),
      ]);

      setProgreso('Guardando apoderado…');
      const alta = await alianzaService.agregarApoderado(datos.alianza.id, {
        dni: campos.dni,
        nombre: campos.nombre.trim(),
        apellido: campos.apellido.trim(),
        genero: campos.genero,
        fechaNacimiento: campos.fechaNacimiento,
        email: (esPrimero ? emailAcceso.trim().toLowerCase() : campos.email.trim()) || undefined,
        telefono: campos.telefono.trim() || undefined,
        partidoIntegranteId: campos.partidoIntegranteId || undefined,
      });
      const apoderadoId: string = alta.data.data.apoderadoId;

      setProgreso('Adjuntando documentación…');
      await alianzaService.cargarActaApoderado(apoderadoId, acta);
      await alianzaService.cargarDniApoderado(apoderadoId, 'frente', frente);
      const ultimo = await alianzaService.cargarDniApoderado(apoderadoId, 'dorso', dorso);

      await mutar(async () => ultimo, 'Apoderado agregado');
      setCampos(camposApoderadoVacios());
      setArchivos(archivosVacios());
      onListo();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'No se pudo agregar el apoderado');
    } finally {
      setGuardando(false);
      setProgreso('');
    }
  };

  return (
    <Tarjeta
      titulo={esPrimero ? 'Apoderado iniciador' : 'Nuevo apoderado'}
      subtitulo="Complete los datos y adjunte la documentación. Todo se guarda junto."
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Etiqueta>DNI *</Etiqueta>
          <input
            type="text"
            inputMode="numeric"
            maxLength={8}
            value={campos.dni}
            onChange={(e) => { cambiar('dni', e.target.value.replace(/\D/g, '')); padron.limpiar(); }}
            onBlur={alSalirDelDni}
            className={inputClass(errores.dni)}
            placeholder="Ej: 30123456"
          />
          <FieldError error={errores.dni} />
        </div>
        <div>
          <Etiqueta>Teléfono</Etiqueta>
          <input
            type="tel"
            value={campos.telefono}
            onChange={(e) => cambiar('telefono', e.target.value.replace(/\D/g, ''))}
            className={inputClass()}
            placeholder="Ej: 2804123456"
          />
        </div>
        <div>
          <Etiqueta>Apellido *</Etiqueta>
          <input
            type="text"
            value={campos.apellido}
            onChange={(e) => cambiar('apellido', e.target.value)}
            disabled={!!padron.persona}
            className={padron.persona ? inputBloqueado : inputClass(errores.apellido)}
          />
          <FieldError error={errores.apellido} />
        </div>
        <div>
          <Etiqueta>Nombre *</Etiqueta>
          <input
            type="text"
            value={campos.nombre}
            onChange={(e) => cambiar('nombre', e.target.value)}
            disabled={!!padron.persona}
            className={padron.persona ? inputBloqueado : inputClass(errores.nombre)}
          />
          <FieldError error={errores.nombre} />
        </div>
        <div>
          <Etiqueta>Género *</Etiqueta>
          <select
            value={campos.genero}
            onChange={(e) => cambiar('genero', e.target.value)}
            disabled={!!padron.persona && !!padron.persona.genero}
            className={padron.persona && padron.persona.genero ? inputBloqueado : inputClass(errores.genero)}
          >
            <option value="">Seleccione…</option>
            {GENEROS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          <FieldError error={errores.genero} />
        </div>
        <div>
          <Etiqueta>Fecha de nacimiento *</Etiqueta>
          <CampoFechaNacimiento
            valor={campos.fechaNacimiento}
            onCambiar={(iso) => cambiar('fechaNacimiento', iso)}
            anioPadron={padron.persona?.anioNacimiento}
            error={errores.fechaNacimiento}
          />
          <FieldError error={errores.fechaNacimiento} />
        </div>
        {/* El iniciador no ve campo de correo: usa el de su cuenta de acceso. */}
        {/*
          Dos casos distintos, no uno con un cartel genérico:
          - Ya tiene usuario en SIREPPAR: no hay correo que cargar. El del apoderado y
            el del usuario son EL MISMO dato (el sistema los sincroniza por DNI), así
            que un campo editable acá los hace divergir en la primera edición. Se
            muestra enmascarado, igual que en la cuenta de acceso del iniciador, y por
            eso tampoco corresponde la leyenda de "podrán crear nuevos usuarios": esta
            persona ya tiene el suyo.
          - No tiene usuario: el correo es su único canal y ahí sí se pide.
        */}
        {!esPrimero && (
          <div>
            <Etiqueta>Correo</Etiqueta>
            {acceso.tieneUsuario ? (
              <>
                {/* Como texto y enmascarado: el backend nunca manda el correo
                    completo por un endpoint público (ver GET /alianzas/acceso-por-dni). */}
                <p className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 font-mono text-gray-700 break-all">
                  {acceso.emailMascarado || '•••••'}
                </p>
                <p className="text-xs text-green-800 mt-1">
                  Esta persona ya tiene usuario en SIREPPAR: las notificaciones del trámite van a esa cuenta,
                  y con ella va a poder administrar la alianza.
                </p>
              </>
            ) : (
              <>
                <input
                  type="email"
                  value={campos.email}
                  onChange={(e) => { cambiar('email', e.target.value); limpiar(); }}
                  onBlur={() => verificar(campos.email, campos.dni)}
                  className={inputClass(errores.email || (estadoCorreo.disponible === false ? 'x' : undefined))}
                  placeholder="usuario@ejemplo.com"
                />
                <AvisoCorreo estado={estadoCorreo} />
                <p className="text-xs text-gray-500 mt-1">
                  {acceso.consultando
                    ? 'Verificando si esta persona ya tiene usuario…'
                    : 'Dentro del sistema podrán crearle un usuario.'}
                </p>
                <FieldError error={errores.email} />
              </>
            )}
          </div>
        )}
        <div>
          <Etiqueta>Partido que lo designa</Etiqueta>
          <select value={campos.partidoIntegranteId} onChange={(e) => cambiar('partidoIntegranteId', e.target.value)} className={inputClass()}>
            <option value="">Sin especificar</option>
            {datos.integrantes.map((i) => (
              <option key={i.id} value={i.id}>{i.miembro?.denominacion || i.miembroNoMigradoDenominacion}</option>
            ))}
          </select>
        </div>
      </div>

      {/* La cuenta de acceso del iniciador se pide acá mismo: es su correo y su
          contraseña, no una configuración aparte de la alianza. */}
      {esPrimero && (
        <SeccionCuentaAcceso
          dni={campos.dni}
          acceso={acceso}
          mutar={mutar}
          errores={erroresPaso}
          errorEmailLocal={errores.emailAcceso}
          credenciales={credenciales}
          setCredenciales={setCredenciales}
        />
      )}

      <div className="border-t mt-6 pt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Documentación</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <CampoArchivo
            etiqueta="Acta de designación *"
            archivo={archivos.acta}
            onElegir={(f) => setArchivos((a) => ({ ...a, acta: f }))}
            error={errores.acta}
          />
          <CampoArchivo
            etiqueta="📷 DNI Frente *"
            archivo={archivos.dniFrente}
            onElegir={(f) => setArchivos((a) => ({ ...a, dniFrente: f }))}
            error={errores.dniFrente}
          />
          <CampoArchivo
            etiqueta="📷 DNI Dorso *"
            archivo={archivos.dniDorso}
            onElegir={(f) => setArchivos((a) => ({ ...a, dniDorso: f }))}
            error={errores.dniDorso}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-6">
        <button type="button" onClick={guardar} disabled={guardando} className={botonPrimario}>
          {guardando && <Spinner claro />}
          {guardando ? (progreso || 'Guardando…') : 'Guardar apoderado'}
        </button>
        {onCancelar && (
          <button type="button" onClick={onCancelar} className={botonSecundario}>Cancelar</button>
        )}
      </div>
    </Tarjeta>
  );
}

/** Casillero de documento con marca de estado. Lo usa el paso 5. */
function FilaDocumento({
  etiqueta,
  documento,
  onArchivo,
}: {
  etiqueta: string;
  documento?: DocumentoAlianza;
  onArchivo: (archivo: AlianzaArchivoSubido) => Promise<void>;
}) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${documento ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Marca ok={!!documento} />
        <span className="text-sm font-medium text-gray-800">{etiqueta}</span>
      </div>
      <p className="text-xs text-gray-600 truncate mb-2">{documento ? documento.nombreOriginal : 'Pendiente'}</p>
      <BotonArchivo etiqueta={documento ? 'Reemplazar' : 'Cargar'} onArchivo={onArchivo} />
    </div>
  );
}

// =============================================================================
// PASO 5 — Órganos de la alianza
// =============================================================================

function Paso5Organos({ datos, errores, mutar }: PropsPaso) {
  const [nuevoOrgano, setNuevoOrgano] = useState('');

  const actasPorOrgano = useMemo(() => {
    const mapa = new Map<string, DocumentoAlianza>();
    datos.documentos
      .filter((d) => d.tipo === 'ACTA_DESIGNACION_ORGANO_ALIANZA' && d.dniPersona?.startsWith('ORGANO:'))
      .forEach((d) => {
        const id = (d.dniPersona as string).slice('ORGANO:'.length);
        if (!mapa.has(id)) mapa.set(id, d);
      });
    return mapa;
  }, [datos.documentos]);

  const crear = async () => {
    if (!nuevoOrgano.trim()) {
      toast.error('Indique la denominación del órgano');
      return;
    }
    const ok = await mutar(() => alianzaService.crearOrgano(datos.alianza.id, nuevoOrgano.trim()), 'Órgano agregado');
    if (ok) setNuevoOrgano('');
  };

  return (
    <>
      <div className="bg-white border border-gray-200 border-l-4 border-l-purple-700 p-4 rounded-r-lg mb-5 text-sm text-purple-900 shadow-sm">
        <p className="font-semibold mb-1">Sobre las autoridades de los órganos</p>
        <p className="text-purple-800">
          Cargue los organos con su Acta de designación, los datos de las autoridades y adjunte el frente y el dorso del DNI de cada uno.
        </p>
      </div>

      {datos.organos.map((organo) => (
        <TarjetaOrgano
          key={organo.id}
          organo={organo}
          datos={datos}
          mutar={mutar}
          acta={actasPorOrgano.get(organo.id)}
        />
      ))}

      <FieldError error={errores['organo']} />

      {/* Alta de un órgano adicional, con el mismo diseño que reempadronamiento:
          caja de borde punteado, el nombre y "Agregar". Sin paso intermedio de
          "crear": se agrega con su nombre y se completa adentro. */}
      <section className="mt-6 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
        <p className="text-gray-600 font-medium mb-3">+ Agregar órgano adicional</p>
        <div className="flex flex-wrap justify-center gap-2">
          <input
            type="text"
            value={nuevoOrgano}
            onChange={(e) => setNuevoOrgano(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && crear()}
            placeholder="Nombre del órgano (ej.: Mesa Directiva)"
            maxLength={120}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-72"
          />
          <button type="button" onClick={crear} className="px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 text-sm">
            Agregar
          </button>
        </div>
      </section>
    </>
  );
}

function TarjetaOrgano({
  organo,
  datos,
  mutar,
  acta,
}: {
  organo: Organo;
  datos: AlianzaCompleta;
  mutar: Mutar;
  acta?: DocumentoAlianza;
}) {
  const nombre = organo.denominacionPersonalizada || organo.denominacion;
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState(nombre);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  const nombreIntegrante = (id?: string) => {
    if (!id) return null;
    const i = datos.integrantes.find((x) => x.id === id);
    return i ? (i.miembro?.denominacion || i.miembroNoMigradoDenominacion) : null;
  };

  const guardarNombre = async () => {
    if (!nombreNuevo.trim()) {
      toast.error('La denominación del órgano no puede quedar vacía');
      return;
    }
    const ok = await mutar(() => alianzaService.renombrarOrgano(organo.id, nombreNuevo.trim()), 'Órgano renombrado');
    if (ok) setEditandoNombre(false);
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 border-l-4 border-l-purple-700 mb-5">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        {editandoNombre ? (
          <div className="flex flex-wrap items-center gap-2 flex-1">
            <input
              type="text"
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              className={`${inputBase} border-gray-300 max-w-sm`}
              maxLength={120}
            />
            <button type="button" onClick={guardarNombre} className={botonPrimario}>Guardar</button>
            <button type="button" onClick={() => { setEditandoNombre(false); setNombreNuevo(nombre); }} className={botonSecundario}>
              Cancelar
            </button>
          </div>
        ) : (
          <div>
            <h2 className="font-semibold text-gray-900">{nombre}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {organo.esObligatorio ? '🔒 Órgano obligatorio de la alianza · se puede renombrar, no eliminar' : 'Órgano opcional'}
            </p>
          </div>
        )}
        {!editandoNombre && (
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setEditandoNombre(true)} className="text-xs text-purple-700 hover:text-purple-800">
              Renombrar
            </button>
            {!organo.esObligatorio && (
              <button
                type="button"
                onClick={() => mutar(() => alianzaService.eliminarOrgano(organo.id), 'Órgano eliminado')}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Eliminar órgano
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* La documentación del órgano va PRIMERO: se carga el acta de designación
            y después se cargan las autoridades que esa acta designa. */}
        <div className={`rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3 ${acta ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <Marca ok={!!acta} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">Acta de designación del órgano</p>
            <p className="text-xs text-gray-600 truncate">{acta ? `${acta.nombreOriginal} · versión ${acta.version}` : 'Pendiente de carga'}</p>
          </div>
          <BotonArchivo
            etiqueta={acta ? 'Reemplazar' : 'Cargar'}
            onArchivo={(archivo) => mutar(() => alianzaService.cargarActaOrgano(organo.id, archivo), 'Acta cargada').then(() => undefined)}
          />
        </div>

        {organo.autoridades.length === 0 ? (
          <p className="text-sm text-red-600">
            Este órgano todavía no tiene autoridades. No puede quedar vacío: cárguele al menos una
            {!organo.esObligatorio && ', o elimine el órgano'}.
          </p>
        ) : (
          <div className="space-y-3">
            {organo.autoridades.map((m) => (
              <FilaAutoridadOrgano
                key={m.id}
                autoridad={m}
                datos={datos}
                mutar={mutar}
                designante={nombreIntegrante(datos.borrador.designantes.autoridadesOrgano[m.id])}
              />
            ))}
          </div>
        )}

        {mostrarFormulario ? (
          <FormularioAutoridad
            organo={organo}
            datos={datos}
            mutar={mutar}
            onListo={() => setMostrarFormulario(false)}
            onCancelar={() => setMostrarFormulario(false)}
          />
        ) : (
          /* Mismo diseño que reempadronamiento: botón de ancho completo con borde
             punteado, al pie del listado de autoridades. */
          <button
            type="button"
            onClick={() => setMostrarFormulario(true)}
            className="w-full py-2 border-2 border-dashed border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 transition"
          >
            + Agregar autoridad
          </button>
        )}

      </div>
    </section>
  );
}


/**
 * Una autoridad ya cargada, con el diseño compacto de los wizards de
 * reempadronamiento y reconocimiento: una fila por persona con los campos chicos y
 * la documentación del DNI abajo, separada por una línea.
 *
 * Los datos son EDITABLES: los escribió quien completa el formulario, no el padrón,
 * así que se pueden corregir. Cada campo se guarda al salir de él.
 */
function FilaAutoridadOrgano({
  autoridad,
  datos,
  mutar,
  designante,
}: {
  autoridad: AutoridadOrgano;
  datos: AlianzaCompleta;
  mutar: Mutar;
  designante: string | null;
}) {
  const mandato = autoridad.mandatos[0];
  const esTitular = mandato?.esTitular ?? true;
  const designanteGuardado = datos.borrador.designantes.autoridadesOrgano[autoridad.id] || '';

  const docDe = (tipo: string) => datos.documentos.find((d) => d.tipo === tipo && d.dniPersona === autoridad.dni);
  const frente = docDe('DNI_FRENTE_AUTORIDAD');
  const dorso = docDe('DNI_DORSO_AUTORIDAD');

  // Todos los campos quedan grises y deshabilitados: la autoridad ya está cargada y
  // sólo se modifica quitándola y volviéndola a cargar. Ver la nota de
  // TarjetaApoderado: es el mismo motivo (los archivos cuelgan del DNI).
  const inputChico = 'w-full px-2 py-1.5 border border-gray-200 bg-gray-100 text-gray-500 rounded text-sm cursor-not-allowed';

  return (
    <div className="bg-gray-50 rounded-lg p-3 relative border">
      <button
        type="button"
        onClick={() => mutar(() => alianzaService.quitarAutoridadOrgano(autoridad.id), 'Autoridad quitada')}
        className="absolute top-2 right-2 px-2 py-1 bg-white border border-red-200 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50"
      >
        Quitar
      </button>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-6 md:pt-0 md:pr-20">
        <div>
          <label className="block text-xs text-gray-500 mb-1">DNI</label>
          <input type="text" value={autoridad.dni} disabled className={inputChico} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Apellido *</label>
          <input type="text" value={autoridad.apellido} disabled className={inputChico} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Nombre *</label>
          <input type="text" value={autoridad.nombre} disabled className={inputChico} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Género *</label>
          <select value={autoridad.genero || ''} disabled className={inputChico}>
            <option value="">Sin especificar</option>
            {GENEROS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Fecha de nacimiento</label>
          <input
            type="date"
            value={autoridad.fechaNacimiento ? autoridad.fechaNacimiento.slice(0, 10) : ''}
            disabled
            className={inputChico}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cargo *</label>
          {/* El cargo puede ser uno de la lista o texto libre (`MandatoAutoridad.cargo`
              es String?), así que se muestra el valor tal cual quedó guardado. */}
          <input
            type="text"
            value={mandato?.cargo ? cargoLabel(mandato.cargo) : '—'}
            disabled
            className={inputChico}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Condición *</label>
          <input
            type="text"
            value={esTitular ? 'Titular' : `Suplente ${mandato?.ordenSuplencia ?? 1}`}
            disabled
            className={inputChico}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Partido que lo designa</label>
          <select value={designanteGuardado} disabled className={inputChico}>
            <option value="">Sin especificar</option>
            {datos.integrantes.map((i) => (
              <option key={i.id} value={i.id}>{i.miembro?.denominacion || i.miembroNoMigradoDenominacion}</option>
            ))}
          </select>
        </div>
      </div>

      {designante && <p className="text-xs text-gray-500 mt-2">Designado por {designante}</p>}

      <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3">
        {([['frente', frente], ['dorso', dorso]] as const).map(([cara, documento]) => (
          <div key={cara}>
            <label className="block text-xs text-gray-500 mb-1">DNI {cara === 'frente' ? 'Frente' : 'Dorso'} *</label>
            {documento
              ? <span className="text-xs text-green-700 truncate block">✓ {documento.nombreOriginal}</span>
              : <span className="text-xs text-red-600">Pendiente</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Alta de una autoridad en una sola pasada, con el diseño de reconocimiento: los
 * datos de la persona los escribe quien completa el formulario (no hay consulta al
 * padrón ni precarga) y el frente y el dorso del DNI se piden en ese mismo momento.
 *
 * La única validación de identidad es que la persona no esté ya en ESTE órgano. No
 * se exige que esté en el padrón provincial: una autoridad de alianza puede no
 * estarlo. Puede, en cambio, integrar otros órganos de la misma alianza.
 */
function FormularioAutoridad({
  organo,
  datos,
  mutar,
  onListo,
  onCancelar,
}: {
  organo: Organo;
  datos: AlianzaCompleta;
  mutar: Mutar;
  onListo: () => void;
  onCancelar: () => void;
}) {
  const [campos, setCampos] = useState({
    dni: '',
    apellido: '',
    nombre: '',
    genero: '',
    fechaNacimiento: '',
    cargo: '',
    cargoPersonalizado: '',
    esTitular: true,
    ordenSuplencia: '1',
    partidoIntegranteId: '',
  });
  const [archivos, setArchivos] = useState<{ frente: ArchivoElegido; dorso: ArchivoElegido }>({ frente: null, dorso: null });
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [progreso, setProgreso] = useState('');

  const padron = usePersonaPadron();

  const cambiar = (campo: string, valor: unknown) => {
    setCampos((c) => ({ ...c, [campo]: valor }));
    if (errores[campo]) setErrores((e) => { const n = { ...e }; delete n[campo]; return n; });
  };

  /** Al salir del DNI: si la persona está en el padrón, sus datos mandan. */
  const alSalirDelDni = async () => {
    const persona = await padron.consultar(campos.dni);
    if (!persona) return;
    setCampos((c) => ({
      ...c,
      apellido: persona.apellido,
      nombre: persona.nombre,
      genero: persona.genero || c.genero,
      fechaNacimiento: persona.anioNacimiento && c.fechaNacimiento
        ? `${persona.anioNacimiento}-${c.fechaNacimiento.slice(5)}`
        : c.fechaNacimiento,
    }));
    setErrores((e) => { const n = { ...e }; delete n.apellido; delete n.nombre; delete n.genero; return n; });
  };

  const cargoEfectivo = () => (campos.cargo === 'OTRO' ? campos.cargoPersonalizado : campos.cargo).trim();

  const validar = (): boolean => {
    const err: Record<string, string> = {};
    const dni = campos.dni.replace(/\D/g, '');
    if (dni.length < 7 || dni.length > 8) err.dni = 'El DNI debe tener 7 u 8 dígitos';
    // Única validación de identidad: no repetir persona en el mismo órgano.
    else if (organo.autoridades.some((a) => a.dni === dni)) {
      err.dni = 'Esa persona ya integra este órgano';
    }
    if (!campos.apellido.trim()) err.apellido = 'El apellido es obligatorio';
    if (!campos.nombre.trim()) err.nombre = 'El nombre es obligatorio';
    if (!campos.genero) err.genero = 'El género es obligatorio';
    if (!cargoEfectivo()) err.cargo = 'El cargo es obligatorio';
    if (!archivos.frente) err.frente = 'El frente del DNI es obligatorio';
    if (!archivos.dorso) err.dorso = 'El dorso del DNI es obligatorio';
    setErrores(err);
    return Object.keys(err).length === 0;
  };

  const guardar = async () => {
    if (!validar()) {
      toast.error('Revise los campos marcados en rojo');
      return;
    }
    setGuardando(true);
    try {
      // Los archivos primero: si el almacenamiento falla, no queda una persona
      // cargada sin su DNI.
      setProgreso('Subiendo archivos…');
      const [frente, dorso] = await Promise.all([
        subirArchivo(archivos.frente as File),
        subirArchivo(archivos.dorso as File),
      ]);

      setProgreso('Guardando autoridad…');
      const alta = await alianzaService.agregarAutoridadOrgano(organo.id, {
        dni: campos.dni.replace(/\D/g, ''),
        apellido: campos.apellido.trim(),
        nombre: campos.nombre.trim(),
        genero: campos.genero,
        fechaNacimiento: campos.fechaNacimiento || undefined,
        cargo: cargoEfectivo(),
        esTitular: campos.esTitular,
        ordenSuplencia: campos.esTitular ? undefined : Number(campos.ordenSuplencia) || 1,
        partidoIntegranteId: campos.partidoIntegranteId || undefined,
      });
      // El backend contesta `autoridadId` (ver agregarAutoridadOrgano). Leer otra
      // clave dejaba `undefined` en la URL del DNI y el 400 del validador de UUID
      // aparecía DESPUÉS de haber creado la autoridad.
      const autoridadId: string = alta.data.data.autoridadId;

      setProgreso('Adjuntando el DNI…');
      await alianzaService.cargarDniAutoridad(autoridadId, 'frente', frente);
      const ultimo = await alianzaService.cargarDniAutoridad(autoridadId, 'dorso', dorso);

      await mutar(async () => ultimo, 'Autoridad agregada');
      onListo();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'No se pudo agregar la autoridad');
    } finally {
      setGuardando(false);
      setProgreso('');
    }
  };

  const inputChico = (error?: string) =>
    `w-full px-2 py-1.5 border rounded text-sm ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;

  return (
    <div className="border border-purple-200 bg-purple-50/40 rounded-lg p-4">
      <p className="text-sm font-medium text-gray-800 mb-3">
        Nueva autoridad de {organo.denominacionPersonalizada || organo.denominacion}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">DNI *</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={8}
            value={campos.dni}
            onChange={(e) => { cambiar('dni', e.target.value.replace(/\D/g, '')); padron.limpiar(); }}
            onBlur={alSalirDelDni}
            className={inputChico(errores.dni)}
            placeholder="Ej: 30123456"
          />
          <FieldError error={errores.dni} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Apellido *</label>
          <input
            type="text"
            value={campos.apellido}
            onChange={(e) => cambiar('apellido', e.target.value)}
            disabled={!!padron.persona}
            className={padron.persona ? inputChicoBloqueado : inputChico(errores.apellido)}
          />
          <FieldError error={errores.apellido} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Nombre *</label>
          <input
            type="text"
            value={campos.nombre}
            onChange={(e) => cambiar('nombre', e.target.value)}
            disabled={!!padron.persona}
            className={padron.persona ? inputChicoBloqueado : inputChico(errores.nombre)}
          />
          <FieldError error={errores.nombre} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Género *</label>
          <select
            value={campos.genero}
            onChange={(e) => cambiar('genero', e.target.value)}
            disabled={!!padron.persona && !!padron.persona.genero}
            className={padron.persona && padron.persona.genero ? inputChicoBloqueado : `${inputChico(errores.genero)} bg-white`}
          >
            <option value="">Seleccione…</option>
            {GENEROS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          <FieldError error={errores.genero} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Fecha de nacimiento</label>
          <CampoFechaNacimiento
            valor={campos.fechaNacimiento}
            onCambiar={(iso) => cambiar('fechaNacimiento', iso)}
            anioPadron={padron.persona?.anioNacimiento}
            chico
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cargo *</label>
          <select value={campos.cargo} onChange={(e) => { cambiar('cargo', e.target.value); cambiar('cargoPersonalizado', ''); }} className={`${inputChico(errores.cargo)} bg-white`}>
            <option value="">Seleccione…</option>
            {CARGOS.map((c) => <option key={c} value={c}>{c === 'OTRO' ? 'Otro (especificar)' : cargoLabel(c)}</option>)}
          </select>
          {campos.cargo === 'OTRO' && (
            <input
              type="text"
              value={campos.cargoPersonalizado}
              onChange={(e) => cambiar('cargoPersonalizado', e.target.value)}
              placeholder="Nombre del cargo"
              className={`${inputChico(errores.cargo)} mt-1`}
            />
          )}
          <FieldError error={errores.cargo} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Condición *</label>
          <select
            value={campos.esTitular ? 'titular' : 'suplente'}
            onChange={(e) => cambiar('esTitular', e.target.value === 'titular')}
            className={`${inputChico()} bg-white`}
          >
            <option value="titular">Titular</option>
            <option value="suplente">Suplente</option>
          </select>
          {!campos.esTitular && (
            <input
              type="number"
              min={1}
              max={99}
              value={campos.ordenSuplencia}
              onChange={(e) => cambiar('ordenSuplencia', e.target.value)}
              title="Orden de suplencia"
              className={`${inputChico()} mt-1`}
            />
          )}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Partido que lo designa</label>
          <select value={campos.partidoIntegranteId} onChange={(e) => cambiar('partidoIntegranteId', e.target.value)} className={`${inputChico()} bg-white`}>
            <option value="">Sin especificar</option>
            {datos.integrantes.map((i) => (
              <option key={i.id} value={i.id}>{i.miembro?.denominacion || i.miembroNoMigradoDenominacion}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3">
        {([['frente', 'Frente'], ['dorso', 'Dorso']] as const).map(([cara, etiqueta]) => {
          const file = archivos[cara];
          return (
            <div key={cara}>
              <label className="block text-xs text-gray-500 mb-1">DNI {etiqueta} *</label>
              <div className="flex items-center gap-2">
                {file && <span className="text-xs text-green-700 truncate max-w-[140px]">✓ {file.name}</span>}
                <label className="cursor-pointer text-xs font-medium text-purple-700 hover:text-purple-800">
                  {file ? 'Cambiar' : 'Adjuntar'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      setArchivos((a) => ({ ...a, [cara]: e.target.files?.[0] || null }));
                      setErrores((prev) => { const n = { ...prev }; delete n[cara]; return n; });
                    }}
                  />
                </label>
              </div>
              <FieldError error={errores[cara]} />
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button type="button" onClick={guardar} disabled={guardando} className={botonPrimario}>
          {guardando && <Spinner claro />}
          {guardando ? (progreso || 'Guardando…') : 'Agregar autoridad'}
        </button>
        <button type="button" onClick={onCancelar} className={botonSecundario}>Cancelar</button>
      </div>
    </div>
  );
}

// =============================================================================
// PASO 6 — Revisión y envío
// =============================================================================

function Paso6Revision({
  datos,
  alianzaId,
  credenciales,
  setCredenciales,
  credencialesDefinidas,
  reutilizaCuenta,
  errores,
  aceptaDeclaracion,
  setAceptaDeclaracion,
  enviando,
  onEnviar,
  onEditar,
}: {
  datos: AlianzaCompleta;
  alianzaId: string;
  credenciales: { email: string; password: string; passwordConfirmacion: string };
  setCredenciales: React.Dispatch<React.SetStateAction<{ email: string; password: string; passwordConfirmacion: string }>>;
  credencialesDefinidas: boolean;
  /** El titular del acceso YA tiene usuario: no se crea cuenta ni credenciales. */
  reutilizaCuenta: boolean;
  errores: Record<string, string>;
  aceptaDeclaracion: boolean;
  setAceptaDeclaracion: (v: boolean) => void;
  enviando: boolean;
  onEnviar: () => void;
  onEditar: (paso: number) => void;
}) {
  const [validaciones, setValidaciones] = useState<Validacion[]>([]);
  const [completo, setCompleto] = useState(false);
  const [cargandoValidaciones, setCargandoValidaciones] = useState(true);

  useEffect(() => {
    setCargandoValidaciones(true);
    alianzaService
      .validaciones(alianzaId)
      .then((r) => {
        setValidaciones(r.data.data.validaciones);
        setCompleto(r.data.data.completo);
      })
      .catch(() => toast.error('No se pudieron cargar las validaciones'))
      .finally(() => setCargandoValidaciones(false));
  }, [alianzaId, datos]);

  /** Sólo lo que impide enviar: `aviso` informa y no bloquea, `ok` ya está. */
  const pendientes = validaciones.filter((v) => v.estado === 'falta');

  const titular = datos.apoderados.find((a) => a.id === (datos.borrador.apoderadoAccesoId || datos.apoderados[0]?.id));

  const Editar = ({ paso }: { paso: number }) => (
    <button type="button" onClick={() => onEditar(paso)} className="text-xs text-purple-700 hover:text-purple-800">
      Editar
    </button>
  );

  return (
    <>
      <Tarjeta titulo="Alianza" accion={<Editar paso={1} />}>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><dt className="text-gray-500">Denominación</dt><dd className="text-gray-900 font-medium">{datos.alianza.denominacion}</dd></div>
          <div><dt className="text-gray-500">Sigla</dt><dd className="text-gray-900">{datos.alianza.sigla || '—'}</dd></div>
          <div><dt className="text-gray-500">Ámbito territorial</dt><dd className="text-gray-900">{tipoLabel(datos.alianza.tipo)}</dd></div>
          <div><dt className="text-gray-500">Convocatoria</dt><dd className="text-gray-900">{datos.alianza.convocatoria?.denominacion || '—'}</dd></div>
          {datos.borrador.atributos.colores.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="text-gray-500">Colores</dt>
              <dd className="flex gap-2 mt-1">
                {datos.borrador.atributos.colores.map((hex) => (
                  <span key={hex} className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: hex }} title={hex} />
                ))}
              </dd>
            </div>
          )}
        </dl>
      </Tarjeta>

      <Tarjeta titulo="Domicilio legal" accion={<Editar paso={1} />}>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><dt className="text-gray-500">Domicilio</dt><dd className="text-gray-900">{datos.alianza.domicilioLegal || '—'}</dd></div>
          <div><dt className="text-gray-500">Localidad</dt><dd className="text-gray-900">{datos.alianza.localidad || '—'}</dd></div>
          <div><dt className="text-gray-500">Código postal</dt><dd className="text-gray-900">{datos.alianza.codigoPostal || '—'}</dd></div>
          <div><dt className="text-gray-500">Correo institucional</dt><dd className="text-gray-900">{datos.alianza.emailOficial || '—'}</dd></div>
        </dl>
      </Tarjeta>

      <Tarjeta titulo={`Partidos integrantes (${datos.integrantes.length})`} accion={<Editar paso={0} />}>
        <ul className="text-sm text-gray-800 space-y-1">
          {datos.integrantes.map((i) => (
            <li key={i.id} className="flex items-center gap-2">
              <Marca ok={!!i.actaVoluntadDocumentoId} />
              <span>
                {i.miembro?.denominacion || i.miembroNoMigradoDenominacion}{' '}
                <span className="text-gray-500 text-xs">
                  ({tipoLabel(i.miembro?.tipo || i.miembroNoMigradoTipo)}
                  {!i.miembroId ? ' · no registrado en SIREPPAR' : ''})
                </span>
              </span>
            </li>
          ))}
        </ul>
      </Tarjeta>

      <Tarjeta titulo={`Documentación adjunta (${datos.documentos.filter((d) => !d.tipo.startsWith('DNI_')).length})`} accion={<Editar paso={2} />}>
        <ul className="text-sm text-gray-800 space-y-1">
          {datos.documentos.filter((d) => !d.tipo.startsWith('DNI_')).map((d) => (
            <li key={d.id} className="truncate">• {d.titulo} — <span className="text-gray-500 text-xs">{d.nombreOriginal}</span></li>
          ))}
        </ul>
      </Tarjeta>

      <Tarjeta titulo="Apoderados y acceso al sistema" accion={<Editar paso={3} />}>
        <ul className="text-sm text-gray-800 space-y-1">
          {datos.apoderados.map((a) => (
            <li key={a.id}>
              • {a.apellido}, {a.nombre} — DNI {a.dni}
              {/* "Titular del acceso" se leía como "el único que entra", y no es eso:
                  es el único al que se le CREA una cuenta acá. Los demás apoderados que
                  ya tengan usuario administran la alianza con el suyo. */}
              {a.id === titular?.id && (
                <span className="text-xs text-purple-700 ml-1">
                  {reutilizaCuenta ? '(entra con su usuario actual)' : '(se le crea la cuenta de acceso)'}
                </span>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
          {/*
            Tres estados, no dos. Antes, con el titular que ya tenía usuario,
            `credencialesDefinidas` era true y el cartel decía "Credenciales definidas
            para" con el correo VACÍO, porque en ese caso el formulario no lo pide y no
            se crea ninguna credencial.
          */}
          {reutilizaCuenta ? (
            <p className="text-sm text-purple-900">
              El titular del acceso <strong>ya tiene usuario en SIREPPAR</strong>: entra con esa cuenta y no se
              crean credenciales nuevas. Los demás apoderados que ya tengan usuario van a poder administrar
              la alianza con el mismo usuario.
            </p>
          ) : credencialesDefinidas ? (
            <p className="text-sm text-purple-900">
              Se crea <strong>una sola cuenta nueva</strong>, la de <strong>{credenciales.email}</strong>: las
              credenciales definidas se envían a ese correo al crearse la solicitud. Los demás apoderados que ya
              tengan usuario en SIREPPAR administran la alianza con el mismo usuario; a los que no tengan, se les puede
              crear uno dentro del sistema.
            </p>
          ) : (
            <>
              <p className="text-sm text-purple-900 font-medium mb-1">Defina las credenciales de acceso</p>
              <p className="text-xs text-purple-800 mb-3">
                La contraseña no se guarda en el borrador, así que hay que definirla otra vez antes de enviar.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Etiqueta>Correo de acceso *</Etiqueta>
                  <input type="email" value={credenciales.email} onChange={(e) => setCredenciales((c) => ({ ...c, email: e.target.value }))} className={inputClass(errores['acceso.email'])} />
                  <FieldError error={errores['acceso.email']} />
                </div>
                <div>
                  <Etiqueta>Contraseña *</Etiqueta>
                  <input type="password" value={credenciales.password} onChange={(e) => setCredenciales((c) => ({ ...c, password: e.target.value }))} className={inputClass(errores['acceso.password'])} autoComplete="new-password" />
                  <FieldError error={errores['acceso.password']} />
                </div>
                <div>
                  <Etiqueta>Repetir contraseña *</Etiqueta>
                  <input type="password" value={credenciales.passwordConfirmacion} onChange={(e) => setCredenciales((c) => ({ ...c, passwordConfirmacion: e.target.value }))} className={inputClass(errores['acceso.passwordConfirmacion'])} autoComplete="new-password" />
                  <FieldError error={errores['acceso.passwordConfirmacion']} />
                </div>
              </div>
            </>
          )}
        </div>
      </Tarjeta>

      {datos.organos.map((o) => (
        <Tarjeta
          key={o.id}
          titulo={o.denominacionPersonalizada || o.denominacion}
          subtitulo={o.esObligatorio ? 'Órgano obligatorio' : 'Órgano opcional'}
          accion={<Editar paso={4} />}
        >
          {o.autoridades.length === 0 ? (
            <p className="text-sm text-gray-500">Sin autoridades cargados.</p>
          ) : (
            <ul className="text-sm text-gray-800 space-y-1">
              {o.autoridades.map((m) => (
                <li key={m.id}>
                  • {m.mandatos[0]?.cargo || 'Sin cargo'} {m.mandatos[0]?.esTitular ? '(titular)' : '(suplente)'}: {m.apellido}, {m.nombre} — DNI {m.dni}
                </li>
              ))}
            </ul>
          )}
        </Tarjeta>
      ))}

      {/* Validación automática, versión pública: SOLO lo que bloquea el envío. El
          checklist completo —con los requisitos ya cumplidos— es una vista
          interna; acá mostrarlo sólo obliga a leer treinta líneas verdes para
          encontrar la que falta. Los avisos que no bloquean tampoco entran: el
          de integrantes no registrados ya se ve en su propia tarjeta. */}
      <Tarjeta titulo="Validación automática">
        {cargandoValidaciones ? (
          <p className="text-sm text-gray-500 flex items-center"><Spinner />Verificando…</p>
        ) : pendientes.length === 0 ? (
          <p className="text-sm text-green-700 flex items-center gap-2">
            <Marca ok /> No hay requisitos pendientes: la solicitud está en condiciones de enviarse.
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Falta{pendientes.length > 1 ? 'n' : ''} {pendientes.length} requisito{pendientes.length > 1 ? 's' : ''} para
              poder enviar la solicitud:
            </p>
            <ul className="space-y-2">
              {pendientes.map((v) => (
                <li
                  key={v.clave}
                  className="flex items-start gap-3 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm"
                >
                  <span className="font-bold flex-shrink-0">✕</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900">{v.etiqueta}</p>
                    {v.detalle && <p className="text-xs text-gray-600">{v.detalle}</p>}
                  </div>
                  <button type="button" onClick={() => onEditar(v.paso)} className="text-xs text-purple-700 hover:text-purple-800 flex-shrink-0">
                    Ir al paso {v.paso + 1}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Tarjeta>

      <section className="bg-white rounded-xl border-2 border-purple-300 p-5">
        <h2 className="font-semibold text-gray-900 mb-2">Firmar y enviar la solicitud</h2>
        <label className="flex items-start gap-3 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={aceptaDeclaracion}
            onChange={(e) => setAceptaDeclaracion(e.target.checked)}
            className="mt-1"
          />
          <span>
            Declaro bajo juramento que los datos y la documentación cargados son veraces, que las Cartas Orgánicas de
            los partidos integrantes autorizan la formación de alianzas (Art. 26) y que cuento con el consentimiento
            de los restantes apoderados.
          </span>
        </label>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" onClick={onEnviar} disabled={enviando || !completo || !aceptaDeclaracion} className={botonPrimario}>
            {enviando && <Spinner claro />}
            {enviando ? 'Enviando…' : 'Firmar y enviar solicitud'}
          </button>
          {!completo && !cargandoValidaciones && (
            <span className="text-sm text-red-600">Faltan requisitos: revise la validación automática de arriba.</span>
          )}
        </div>
      </section>
    </>
  );
}

// =============================================================================
// Pantallas finales
// =============================================================================

function PantallaExito({
  envio,
  alianzaId,
  denominacion,
}: {
  envio: { expedienteNumero: string; accesoPendienteVinculacion: boolean; constanciaPdfBase64?: string };
  alianzaId: string;
  denominacion: string;
}) {
  const navigate = useNavigate();

  const descargar = () => {
    if (envio.constanciaPdfBase64) {
      const binario = atob(envio.constanciaPdfBase64);
      const bytes = new Uint8Array(binario.length);
      for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank');
      return;
    }
    window.open(alianzaService.urlPdf(alianzaId), '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-green-600 px-6 py-6 text-center">
          <p className="text-4xl">✓</p>
          <h1 className="text-xl font-bold text-white mt-2">Solicitud presentada</h1>
          <p className="text-green-100 text-sm mt-1">{denominacion}</p>
        </div>
        <div className="p-6 space-y-4">
          {/* El código de verificación NO se muestra: queda en la base y en el PDF
              de la constancia, pero no es un dato que el apoderado tenga que
              anotar ni usar. */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500">Expediente</p>
            <p className="font-mono font-bold text-gray-900">{envio.expedienteNumero}</p>
          </div>

          {/* No hay usuarios pendientes de confirmación: el que se crea acá queda
              activo y entra en el momento. El otro caso no es una espera, es que
              esa persona YA tenía usuario con su DNI y sigue usando ese. */}
          {envio.accesoPendienteVinculacion ? (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg text-sm">
              <p className="font-semibold text-green-900 mb-1">Ingrese con el usuario que ya tenía</p>
              <p className="text-green-800">
                Su DNI ya estaba registrado como usuario del sistema, así que no se creó una cuenta nueva: entre con la
                que usa habitualmente. La Secretaría Electoral Permanente le va a vincular esta alianza a esa cuenta.
              </p>
            </div>
          ) : (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg text-sm">
              <p className="font-semibold text-green-900 mb-1">Su usuario ya está activo</p>
              <p className="text-green-800">
                Ingrese al sistema con el correo y la contraseña que definió y
                siga desde ahí el estado del expediente. Le enviamos esos datos por correo además de la constancia de presentación.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button type="button" onClick={descargar} className={botonPrimario}>Descargar comprobante</button>
            <button type="button" onClick={() => navigate('/login')} className={botonSecundario}>Volver al inicio</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpedientePresentado({
  expediente,
  denominacion,
  alianzaId,
}: {
  expediente: AlianzaCompleta['expediente'];
  denominacion: string;
  alianzaId: string;
}) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-200 p-6">
        <h1 className="text-lg font-bold text-gray-900">Esta solicitud ya fue presentada</h1>
        <p className="text-sm text-gray-600 mt-2">
          La alianza <strong>{denominacion}</strong> tiene el expediente{' '}
          <span className="font-mono">{expediente.numero}</span> en estado {expediente.estado}. El trámite público de
          carga se cerró: los cambios que pida la Secretaría Electoral Permanente se responden desde el expediente,
          ingresando al sistema.
        </p>
        <div className="flex flex-wrap gap-3 mt-5">
          <button type="button" onClick={() => window.open(alianzaService.urlPdf(alianzaId), '_blank')} className={botonSecundario}>
            Descargar comprobante
          </button>
          <button type="button" onClick={() => navigate('/login')} className={botonPrimario}>Ingresar al sistema</button>
        </div>
      </div>
    </div>
  );
}
